<template>
  <div
    class="relationship-network-map"
    :class="{
      fullscreen: fullscreen,
      compact: isCompactView,
      'has-selection': selectedNode,
      'has-signature-highlight': hasSignatureHighlight,
      'detail-overview': detailMode === 'overview',
      'detail-cluster': detailMode === 'cluster',
      'detail-person': detailMode === 'detail',
      'detail-focus': detailMode === 'focus',
    }"
  >
    <div v-if="showToolbar" class="network-toolbar">
      <div class="network-summary">
        <span class="summary-primary">{{ formatNumber(visibleNodeCount) }} people</span>
        <span class="summary-divider">/</span>
        <span>{{ formatNumber(visibleEdgeCount) }} connections</span>
        <span v-if="hiddenEdgeCount > 0" class="summary-muted">
          {{ formatNumber(hiddenEdgeCount) }} hidden
        </span>
      </div>
      <div class="network-controls">
        <div class="strength-control" :title="strengthControlTitle">
          <span class="control-label">Links</span>
          <div class="link-filter-toggle" role="group" aria-label="Relationship link filter">
            <button
              v-for="option in linkFilterOptions"
              :key="option.key"
              type="button"
              class="link-filter-option"
              :class="{ active: option.key === activeLinkFilterKey, single: linkFilterOptions.length === 1 }"
              :aria-pressed="option.key === activeLinkFilterKey ? 'true' : 'false'"
              :disabled="option.disabled"
              :title="option.description"
              @click="setLinkFilter(option)"
            >
              <span>{{ option.label }}</span>
              <strong>{{ formatNumber(option.count) }}</strong>
            </button>
          </div>
        </div>
        <button
          v-if="selectedNode"
          type="button"
          class="map-icon-button"
          title="Clear focus"
          aria-label="Clear focus"
          @click="clearFocus"
        >
          <i class="el-icon-close"></i>
        </button>
        <button
          type="button"
          class="map-icon-button"
          title="Zoom in"
          @click="zoomIn"
        >
          <i class="el-icon-zoom-in"></i>
        </button>
        <button
          type="button"
          class="map-icon-button"
          title="Zoom out"
          @click="zoomOut"
        >
          <i class="el-icon-zoom-out"></i>
        </button>
        <button
          type="button"
          class="map-icon-button"
          title="Reset view"
          @click="resetCamera"
        >
          <i class="el-icon-refresh-right"></i>
        </button>
      </div>
    </div>

    <div class="map-stage-wrap" :style="stageStyle">
      <div ref="stage" class="sigma-stage"></div>
      <canvas
        v-show="showEdgeFlowCanvas"
        ref="flowCanvas"
        class="edge-flow-canvas"
        aria-hidden="true"
      ></canvas>

      <div v-if="showMapHud" class="map-hud">
        <div v-if="showNetworkInsights" class="network-insights-card">
          <div class="insight-item">
            <span class="insight-value">{{ networkDensity }}%</span>
            <span class="insight-label">density</span>
          </div>
          <div class="insight-item">
            <span class="insight-value">{{ formatNumber(strongConnectionCount) }}</span>
            <span class="insight-label">strong</span>
          </div>
          <div class="insight-item">
            <span class="insight-value">{{ averageConnectionStrength }}%</span>
            <span class="insight-label">avg link</span>
          </div>
        </div>
        <div v-if="showNetworkNotice && networkNotice" class="network-notice">
          {{ networkNotice }}
        </div>
        <div v-if="showSignatureHighlightCard" class="signature-highlight-card">
          <span class="signature-highlight-title">{{ signatureHighlightLabel }}</span>
          <span>{{ signatureHighlightCountLabel }}</span>
        </div>
        <div v-if="showDetailModeCard" class="detail-mode-card">
          <span class="detail-mode-name">{{ detailModeLabel }}</span>
          <span>{{ formatNumber(viewportNodeCount) }} in view</span>
        </div>
        <div v-if="showEdgeLegend && edgeLegendItems.length > 0" class="edge-legend-card">
          <div
            v-for="item in edgeLegendItems"
            :key="item.key"
            class="edge-legend-item"
          >
            <i :class="['edge-legend-line', `edge-legend-${item.key}`]"></i>
            <span>{{ item.label }}</span>
          </div>
        </div>
        <div v-if="showClusterChips && clusterChips.length > 0" class="cluster-chip-list">
          <button
            v-for="cluster in clusterChips"
            :key="cluster.name"
            type="button"
            class="cluster-chip"
            @click="zoomToNodes(cluster.nodeIds)"
          >
            <i :style="{ backgroundColor: cluster.color }"></i>
            <span>{{ cluster.name }}</span>
            <strong>{{ cluster.count }}</strong>
          </button>
        </div>
      </div>

      <div v-if="showAttributeScale" class="attribute-scale-card">
        <div class="scale-header">
          <span>{{ selectedAttributeName }}</span>
          <span>{{ formatNumber(distributionTotal) }}</span>
        </div>
        <div class="scale-legend-list" :title="distributionTitle">
          <div
            v-for="item in attributeLegendItems"
            :key="item.key"
            class="scale-legend-item"
            :class="{ muted: item.key === 'unknown' }"
          >
            <i :style="{ backgroundColor: item.color }"></i>
            <span>{{ item.label }}</span>
            <strong>{{ formatNumber(item.count) }}</strong>
          </div>
        </div>
      </div>

      <div v-if="isLoading" class="map-empty-state map-loading-state">
        <div class="loading-spinner" aria-hidden="true"></div>
        <div class="empty-title">{{ loadingTitle }}</div>
        <div class="empty-subtitle">{{ loadingSubtitle }}</div>
      </div>

      <div v-else-if="isEmpty" class="map-empty-state">
        <div class="empty-title">{{ emptyTitle }}</div>
        <div class="empty-subtitle">{{ emptySubtitle }}</div>
      </div>

      <div v-else-if="renderError" class="map-empty-state">
        <div class="empty-title">Map unavailable</div>
        <div class="empty-subtitle">{{ renderError }}</div>
      </div>

      <transition name="focus-avatar">
        <div
          v-if="focusedAvatarStyle"
          :key="selectedNode.id"
          class="focused-node-anchor"
          :style="focusedAvatarStyle"
        >
          <button
            type="button"
            class="focused-node-avatar"
            :aria-label="`View ${selectedNode.label}`"
            @click="$emit('preview-person', selectedNode.person)"
          >
            <img v-if="showFocusedAvatarImage" :src="selectedNode.profileImage" alt="" />
            <span v-else>{{ focusedAvatarFallback }}</span>
          </button>
        </div>
      </transition>

      <transition name="node-panel-transition" mode="out-in">
        <div v-if="showNodePanel && selectedNode" :key="selectedNode.id" class="node-panel">
          <div class="node-panel-header">
            <div class="node-avatar" :class="{ 'blur-sensitive': blurMode }">
              <img v-if="selectedNode.profileImage" :src="selectedNode.profileImage" alt="" />
              <span v-else>{{ selectedNode.shortLabel }}</span>
            </div>
            <div class="node-copy" :class="{ 'blur-sensitive': blurMode }">
              <div class="node-name">{{ selectedNode.label }}</div>
              <div class="node-team">{{ selectedNode.teamName || '-' }}</div>
            </div>
            <div class="score-badge" :style="selectedScoreStyle">
              <span class="score-badge-label">{{ selectedAttributeName }}</span>
              <strong>{{ selectedAttributeValue }}</strong>
            </div>
          </div>
          <div class="node-panel-stats">
            <div>
              <span class="stat-value">{{ selectedConnectionCount }}</span>
              <span class="stat-label">direct</span>
            </div>
            <div v-if="showSecondDegreeStat">
              <span class="stat-value">{{ selectedSecondDegreeConnectionCount }}</span>
              <span class="stat-label">2nd degree</span>
            </div>
            <div>
              <span class="stat-value">{{ selectedAverageStrength }}%</span>
              <span class="stat-label">avg link</span>
            </div>
          </div>
          <div v-if="showExpandedFocusDetails" class="selected-power-row">
            <div class="power-copy">
              <span>Network Power</span>
              <small>Reach and influence from connection strength, position, and network role.</small>
            </div>
            <strong>{{ selectedNode.powerRating }}</strong>
          </div>
          <div class="connections-list">
            <div class="connections-title">Top connections</div>
            <button
              v-for="connection in topConnections"
              :key="connection.node.id"
              type="button"
              class="connection-row"
              @click="focusConnection(connection)"
            >
              <div class="connection-avatar" :class="{ 'blur-sensitive': blurMode }">
                <img v-if="connection.node.profileImage" :src="connection.node.profileImage" alt="" />
                <span v-else>{{ connection.node.shortLabel }}</span>
              </div>
              <div class="connection-copy" :class="{ 'blur-sensitive': blurMode }">
                <div class="connection-name">{{ connection.node.label }}</div>
                <div class="connection-team">{{ connection.node.teamName || '-' }}</div>
              </div>
              <div class="connection-strength">
                <span>{{ connection.percent }}%</span>
                <div class="connection-bar">
                  <i :style="{ width: `${connection.percent}%` }"></i>
                </div>
              </div>
            </button>
            <div v-if="topConnections.length === 0" class="connections-empty">
              No direct connections in this view.
            </div>
          </div>
          <div class="node-panel-actions">
            <el-button
              size="mini"
              class="exit-focus-action"
              icon="el-icon-close"
              @click="clearFocus"
            >
              Exit focus
            </el-button>
            <el-button
              v-if="showExpandedFocusDetails"
              size="mini"
              type="text"
              @click="zoomToSelectedNeighborhood"
            >
              Explore neighborhood
            </el-button>
            <el-button size="mini" type="text" @click="$emit('preview-person', selectedNode.person)">
              View profile
            </el-button>
            <el-button
              v-if="showExpandedFocusDetails"
              size="mini"
              type="text"
              @click="$emit('focus-person', selectedNode.person)"
            >
              Keep focus
            </el-button>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import {
  buildRelationshipGraphModel,
  filterRelationshipGraphModel,
} from '@/utils/relationshipGraph'

const Graph = require('graphology')
const SigmaModule = require('sigma')
const Sigma = SigmaModule.default || SigmaModule
const FA2Layout = require('graphology-layout-forceatlas2/worker')

const BRAND_PURPLE = '#6C30CC'
const BRAND_PURPLE_DARK = '#4E2494'
const BRAND_PURPLE_EDGE = 'rgba(108, 48, 204, 0.28)'
const BRAND_PURPLE_EDGE_LIGHT = 'rgba(108, 48, 204, 0.16)'
const BRAND_PURPLE_EDGE_STRONG = 'rgba(108, 48, 204, 0.82)'
const BRAND_PURPLE_EDGE_DEFAULT = '#D8C7F7'
const DEFAULT_NODE_COLOR = '#747474'
const STRONG_LINK_THRESHOLD = 0.8
const EDGE_FLOW_MAX_EDGES = 90
const ATTRIBUTE_BAND_COLORS = {
  red: '#F44336',
  npsRed: '#BF3328',
  yellow: '#FBC02D',
  green: '#66BB6A',
  npsGreen: '#3B8D3E',
  unknown: '#9CA3AF',
}

const ATTRIBUTE_LEGENDS = {
  impact_rating: {
    order: ['red', 'yellow', 'green', 'unknown'],
    labels: { red: 'At risk', yellow: 'Mixed', green: 'Strong', unknown: 'No score' },
  },
  happiness: {
    order: ['red', 'yellow', 'green', 'unknown'],
    labels: { red: 'Low', yellow: 'Mixed', green: 'High', unknown: 'No score' },
  },
  'well-being': {
    order: ['red', 'yellow', 'green', 'unknown'],
    labels: { red: 'Low', yellow: 'Mixed', green: 'High', unknown: 'No score' },
  },
  NPS: {
    order: ['red', 'yellow', 'green', 'unknown'],
    labels: { red: 'Detractor', yellow: 'Passive', green: 'Promoter', unknown: 'No answer' },
    colors: { red: ATTRIBUTE_BAND_COLORS.npsRed, green: ATTRIBUTE_BAND_COLORS.npsGreen },
  },
  eNPS: {
    order: ['red', 'yellow', 'green', 'unknown'],
    labels: { red: 'Detractor', yellow: 'Passive', green: 'Promoter', unknown: 'No answer' },
    colors: { red: ATTRIBUTE_BAND_COLORS.npsRed, green: ATTRIBUTE_BAND_COLORS.npsGreen },
  },
  culture_index: {
    order: ['red', 'yellow', 'green', 'unknown'],
    labels: { red: 'Low', yellow: 'Mixed', green: 'Strong', unknown: 'No answer' },
  },
  toxic_behaviors: {
    order: ['green', 'yellow', 'red', 'unknown'],
    labels: { green: 'Low', yellow: 'Some', red: 'High', unknown: 'No answer' },
  },
}

const EDGE_FLOW_PATTERNS = {
  standard: {
    cycleDuration: 3.8,
    alpha: 0.34,
    lineWidth: 1.15,
    radius: 1.8,
    trail: 18,
    nodeGap: 7,
    arrivalRadius: 5,
    arrivalAlpha: 0.2,
    returnOffset: 0.5,
  },
  strong: {
    cycleDuration: 3.05,
    alpha: 0.48,
    lineWidth: 1.55,
    radius: 2.25,
    trail: 26,
    nodeGap: 8,
    arrivalRadius: 6,
    arrivalAlpha: 0.28,
    returnOffset: 0.44,
  },
  management: {
    cycleDuration: 4.4,
    alpha: 0.28,
    lineWidth: 1.05,
    radius: 1.65,
    trail: 16,
    nodeGap: 7,
    arrivalRadius: 4,
    arrivalAlpha: 0.16,
    returnOffset: 0.56,
  },
}

const EDGE_COLORS = {
  relationship: BRAND_PURPLE,
  management: '#4B5563',
}

const NODE_DISPLAY_MODES = {
  initials_team: true,
  person_team: true,
  initials: true,
  none: true,
  blurred: true,
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value))
}

function easeInOutSine(value) {
  const t = clamp(value, 0, 1)
  return -(Math.cos(Math.PI * t) - 1) / 2
}

function pulseEnvelope(value) {
  return Math.pow(Math.sin(clamp(value, 0, 1) * Math.PI), 1.15)
}

function stableUnitValue(value, salt = '') {
  const input = `${salt}:${value || ''}`
  let hash = 2166136261
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }

  return (hash >>> 0) / 4294967295
}

function normalizeMapLinkMode(value) {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')

  if (normalized === 'meaningful_interactions_only') return 'meaningful_interactions_only'
  if (normalized === 'line_of_management_only' || normalized === 'management_line_only') return 'line_of_management_only'
  return 'all_relational_data'
}

export default {
  name: 'RelationshipNetworkMap',
  props: {
    scopedRows: {
      type: Array,
      default: () => [],
    },
    allRows: {
      type: Array,
      default: () => [],
    },
    selectedAttribute: {
      type: String,
      default: 'happiness',
    },
    linkMode: {
      type: String,
      default: 'all_relational_data',
    },
    nodeDisplayMode: {
      type: String,
      default: 'initials_team',
    },
    focusedEmail: {
      type: String,
      default: '',
    },
    highlightedSignatures: {
      type: Array,
      default: () => [],
    },
    highlightedSignatureGroups: {
      type: Array,
      default: () => [],
    },
    highlightLabel: {
      type: String,
      default: '',
    },
    blurMode: {
      type: Boolean,
      default: false,
    },
    height: {
      type: [Number, String],
      default: 420,
    },
    fullscreen: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    showToolbar: {
      type: Boolean,
      default: true,
    },
    showHud: {
      type: Boolean,
      default: true,
    },
    showAttributeLegend: {
      type: Boolean,
      default: true,
    },
    showNodePanel: {
      type: Boolean,
      default: true,
    },
    includeConnectedOutsideScope: {
      type: Boolean,
      default: true,
    },
    layoutMode: {
      type: String,
      default: 'network',
    },
    lockFocusedNode: {
      type: Boolean,
      default: false,
    },
    showEdgeFlow: {
      type: Boolean,
      default: true,
    },
    maxEdges: {
      type: Number,
      default: 0,
    },
    loadingTitle: {
      type: String,
      default: 'Loading relationship map',
    },
    loadingSubtitle: {
      type: String,
      default: 'Preparing people and connection data.',
    },
    emptyTitle: {
      type: String,
      default: 'Empty team',
    },
    emptySubtitle: {
      type: String,
      default: 'No relationship data is available for this selection.',
    },
  },
  data() {
    return {
      graphModel: null,
      visibleModel: null,
      graph: null,
      renderer: null,
      layout: null,
      layoutStopTimer: null,
      edgeThreshold: 0,
      thresholdTouched: false,
      hoveredNodeId: '',
      hoverClearTimeout: null,
      selectedNodeId: '',
      positionCache: {},
      renderError: '',
      resizeHandler: null,
      resizeObserver: null,
      keydownHandler: null,
      cameraUpdatedHandler: null,
      rendererSettleFrame: null,
      rendererSettleTimers: [],
      renderCycle: 0,
      viewportUpdateFrame: null,
      selectedNodePositionFrame: null,
      viewportRenderSignature: '',
      cameraRatio: 1,
      viewportNodeIds: [],
      selectedNodeViewportPoint: null,
      edgeFlowFrame: null,
      edgeFlowStartedAt: 0,
      edgeFlowCanvasWidth: 0,
      edgeFlowCanvasHeight: 0,
      reducedMotion: false,
      reducedMotionQuery: null,
      reducedMotionHandler: null,
    }
  },
  computed: {
    stageStyle() {
      const height = typeof this.height === 'number' ? `${this.height}px` : this.height
      return { height }
    },
    isCompactView() {
      return !this.fullscreen
    },
    isLoading() {
      return this.loading && !this.renderError
    },
    isEmpty() {
      return !this.isLoading && !this.renderError && this.visibleNodeCount === 0
    },
    normalizedNodeDisplayMode() {
      const normalized = String(this.nodeDisplayMode || '')
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, '_')

      if (NODE_DISPLAY_MODES[normalized]) return normalized
      if (normalized === 'photo' || normalized === 'profile' || normalized === 'profile_photo') return 'initials_team'
      if (normalized === 'initials_and_team' || normalized === 'initials_team_name') return 'initials_team'
      if (normalized === 'name_team' || normalized === 'person_and_team' || normalized === 'name_and_team') return 'person_team'
      return 'initials_team'
    },
    normalizedLayoutMode() {
      const normalized = String(this.layoutMode || '')
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, '_')

      return normalized === 'ego' || normalized === 'profile' || normalized === 'person' ? 'ego' : 'network'
    },
    isEgoLayout() {
      return this.normalizedLayoutMode === 'ego'
    },
    visibleNodeCount() {
      return this.visibleModel ? this.visibleModel.nodes.length : 0
    },
    visibleEdgeCount() {
      return this.visibleModel ? this.visibleModel.edges.length : 0
    },
    showMapHud() {
      return this.showHud && !this.isLoading && !this.renderError && this.visibleNodeCount > 0
    },
    showNetworkInsights() {
      return this.fullscreen
    },
    showNetworkNotice() {
      return this.fullscreen || this.hiddenEdgeCount > 0 || this.detailMode === 'focus'
    },
    showDetailModeCard() {
      return true
    },
    showEdgeLegend() {
      return this.fullscreen
    },
    showClusterChips() {
      return this.fullscreen
    },
    showAttributeScale() {
      return this.showAttributeLegend && this.showMapHud
    },
    showEdgeFlowCanvas() {
      return this.showEdgeFlow && !this.reducedMotion && !this.isLoading && !this.renderError && (this.detailMode === 'detail' || this.detailMode === 'focus')
    },
    showSignatureHighlightCard() {
      return this.hasSignatureHighlight
    },
    showExpandedFocusDetails() {
      return this.fullscreen
    },
    showSecondDegreeStat() {
      return this.fullscreen
    },
    topConnectionLimit() {
      return this.fullscreen ? 5 : 2
    },
    viewportNodeCount() {
      return this.viewportNodeIds.length || this.visibleNodeCount
    },
    viewportNodeLookup() {
      const lookup = {}
      this.viewportNodeIds.forEach(id => {
        lookup[id] = true
      })
      return lookup
    },
    detailMode() {
      const count = this.viewportNodeCount
      if (this.selectedNode) return 'focus'
      if (count <= 28 || (this.cameraRatio < 0.22 && count <= 80)) return 'detail'
      if (count <= 95 || (this.cameraRatio < 0.46 && count <= 160)) return 'cluster'
      return 'overview'
    },
    detailModeLabel() {
      const labels = {
        overview: 'Overview',
        cluster: 'Cluster view',
        detail: 'Person detail',
        focus: 'Focus mode',
      }
      return labels[this.detailMode] || 'Overview'
    },
    adaptiveNodeScale() {
      const count = this.viewportNodeCount
      let scale = 0.96

      if (count > 520) {
        scale = 0.58
      } else if (count > 360) {
        scale = 0.64
      } else if (count > 220) {
        scale = 0.72
      } else if (count > 140) {
        scale = 0.8
      } else if (count > 80) {
        scale = 0.88
      } else if (count <= 28) {
        scale = 1
      }

      if (this.detailMode === 'overview') return Math.min(scale, 0.82)
      if (this.detailMode === 'cluster') return Math.min(Math.max(scale, 0.86), 1)
      if (this.detailMode === 'detail' || this.detailMode === 'focus') return Math.min(Math.max(scale, 0.92), 1.04)
      return scale
    },
    nodeScaleBucket() {
      return Math.round(this.adaptiveNodeScale * 20)
    },
    hiddenEdgeCount() {
      if (!this.graphModel || !this.visibleModel) return 0
      return Math.max(0, this.graphModel.totalEdgeCount - this.visibleModel.edges.length)
    },
    allLinkCount() {
      return this.graphModel ? this.graphModel.edges.length : 0
    },
    strongLinkCount() {
      if (!this.graphModel) return 0
      return this.graphModel.edges.filter(edge => edge.isStrong || Number(edge.weight || 0) >= STRONG_LINK_THRESHOLD).length
    },
    normalizedLinkMode() {
      return normalizeMapLinkMode(this.linkMode)
    },
    isManagementLinkMode() {
      return this.normalizedLinkMode === 'line_of_management_only'
    },
    isMeaningfulLinkMode() {
      return this.normalizedLinkMode === 'meaningful_interactions_only'
    },
    linkFilterOptions() {
      if (this.isManagementLinkMode) {
        return [{
          key: 'reporting',
          label: 'Reporting lines',
          count: this.allLinkCount,
          threshold: 0,
          disabled: true,
          description: 'showing manager relationships only',
        }]
      }

      if (this.isMeaningfulLinkMode) {
        return [{
          key: 'meaningful',
          label: 'Meaningful',
          count: this.allLinkCount,
          threshold: 0,
          disabled: true,
          description: 'showing meaningful interactions only',
        }]
      }

      return [
        {
          key: 'all',
          label: 'All',
          count: this.allLinkCount,
          threshold: 0,
          disabled: false,
          description: 'showing every relationship link in this view',
        },
        {
          key: 'strong',
          label: 'Strong',
          count: this.strongLinkCount,
          threshold: STRONG_LINK_THRESHOLD,
          disabled: this.strongLinkCount === 0,
          description: 'showing only high-strength relationships',
        },
      ]
    },
    activeLinkFilterKey() {
      if (this.isManagementLinkMode) return 'reporting'
      if (this.isMeaningfulLinkMode) return 'meaningful'
      return this.edgeThreshold >= STRONG_LINK_THRESHOLD ? 'strong' : 'all'
    },
    activeLinkFilterOption() {
      return this.linkFilterOptions.find(option => option.key === this.activeLinkFilterKey) || this.linkFilterOptions[0]
    },
    strengthControlTitle() {
      const option = this.activeLinkFilterOption
      return option ? option.description : 'Choose which relationship links are visible.'
    },
    edgeLegendItems() {
      if (!this.visibleModel || this.visibleModel.edges.length === 0) return []
      if (this.isManagementLinkMode) {
        return [{ key: 'management', label: 'Reporting line' }]
      }

      return [
        { key: 'standard', label: 'Standard' },
        { key: 'strong', label: 'Strong' },
      ]
    },
    selectedNode() {
      const id = this.activeNodeId
      if (!id || !this.visibleModel) return null
      return this.visibleModel.nodes.find(node => node.id === id || node.email === id) || null
    },
    activeNodeId() {
      return this.focusedEmail || this.selectedNodeId
    },
    signatureHighlightLookup() {
      return this.buildSignatureLookup(this.highlightedSignatures)
    },
    signatureGroupHighlightLookup() {
      return this.buildSignatureLookup(this.highlightedSignatureGroups)
    },
    hasSignatureHighlight() {
      return Object.keys(this.signatureHighlightLookup).length > 0 || Object.keys(this.signatureGroupHighlightLookup).length > 0
    },
    signatureHighlightedNodeIds() {
      const lookup = {}
      if (!this.visibleModel || !this.hasSignatureHighlight) return lookup

      this.visibleModel.nodes.forEach(node => {
        if (this.nodeMatchesSignatureHighlight(node)) {
          lookup[node.id] = true
        }
      })

      return lookup
    },
    signatureHighlightCount() {
      return Object.keys(this.signatureHighlightedNodeIds).length
    },
    signatureHighlightLabel() {
      const values = Array.isArray(this.highlightedSignatures)
        ? this.highlightedSignatures.filter(Boolean).map(value => String(value).trim()).filter(Boolean)
        : []

      if (this.highlightLabel) return `${this.highlightLabel} highlighted`
      if (values.length === 1) return `${values[0]} highlighted`
      return `${this.formatNumber(values.length)} signatures highlighted`
    },
    signatureHighlightCountLabel() {
      const count = this.signatureHighlightCount
      return `${this.formatNumber(count)} ${count === 1 ? 'person' : 'people'}`
    },
    focusedAvatarStyle() {
      if (!this.selectedNode || !this.selectedNodeViewportPoint) return null
      const color = this.selectedNode.attributeColor || BRAND_PURPLE

      return {
        transform: `translate(${Math.round(this.selectedNodeViewportPoint.x)}px, ${Math.round(this.selectedNodeViewportPoint.y)}px) translate(-50%, -50%)`,
        '--focused-avatar-color': color,
      }
    },
    showFocusedAvatarImage() {
      return Boolean(
        this.selectedNode &&
        this.selectedNode.profileImage &&
        !this.blurMode &&
        this.normalizedNodeDisplayMode !== 'blurred'
      )
    },
    focusedAvatarFallback() {
      if (!this.selectedNode || this.blurMode || this.normalizedNodeDisplayMode === 'blurred') return ''
      return this.selectedNode.shortLabel
    },
    selectedConnectionCount() {
      return this.selectedDirectEdges.length
    },
    nodeLookup() {
      const lookup = {}
      const nodes = this.graphModel ? this.graphModel.nodes : []
      nodes.forEach(node => {
        lookup[node.id] = node
      })
      return lookup
    },
    selectedDirectEdges() {
      if (!this.selectedNode || !this.graphModel) return []
      return this.graphModel.edges.filter(edge => edge.source === this.selectedNode.id || edge.target === this.selectedNode.id)
    },
    selectedSecondDegreeConnectionCount() {
      if (!this.selectedNode || !this.graphModel) return 0
      const direct = new Set()
      const secondDegree = new Set()

      this.selectedDirectEdges.forEach(edge => {
        direct.add(edge.source === this.selectedNode.id ? edge.target : edge.source)
      })

      this.graphModel.edges.forEach(edge => {
        const sourceIsDirect = direct.has(edge.source)
        const targetIsDirect = direct.has(edge.target)
        if (sourceIsDirect && edge.target !== this.selectedNode.id && !direct.has(edge.target)) {
          secondDegree.add(edge.target)
        }
        if (targetIsDirect && edge.source !== this.selectedNode.id && !direct.has(edge.source)) {
          secondDegree.add(edge.source)
        }
      })

      return secondDegree.size
    },
    selectedAverageStrength() {
      if (this.selectedDirectEdges.length === 0) return 0
      const total = this.selectedDirectEdges.reduce((sum, edge) => sum + Number(edge.weight || 0), 0)
      return Math.round((total / this.selectedDirectEdges.length) * 100)
    },
    topConnections() {
      if (!this.selectedNode) return []
      const seen = new Set()
      return this.selectedDirectEdges
        .map(edge => {
          const nodeId = edge.source === this.selectedNode.id ? edge.target : edge.source
          return {
            edge,
            node: this.nodeLookup[nodeId],
            percent: Math.round(Number(edge.weight || 0) * 100),
          }
        })
        .filter(connection => {
          if (!connection.node || seen.has(connection.node.id)) return false
          seen.add(connection.node.id)
          return true
        })
        .sort((a, b) => b.percent - a.percent)
        .slice(0, this.topConnectionLimit)
    },
    selectedAttributeValue() {
      if (!this.selectedNode || this.selectedNode.attributeValue === null) return '-'
      return this.selectedNode.attributeValue
    },
    selectedAttributeLabel() {
      const labels = {
        impact_rating: 'impact',
        happiness: 'happy',
        'well-being': 'WHO-5',
        NPS: 'NPS',
        eNPS: 'eNPS',
        culture_index: 'culture',
        toxic_behaviors: 'toxic',
      }
      return labels[this.selectedAttribute] || 'score'
    },
    selectedAttributeName() {
      const labels = {
        impact_rating: 'Impact rating',
        happiness: 'Happiness',
        'well-being': 'Well-being',
        NPS: 'NPS',
        eNPS: 'eNPS',
        culture_index: 'Culture index',
        toxic_behaviors: 'Toxic behaviors',
      }
      return labels[this.selectedAttribute] || 'Score'
    },
    selectedScoreStyle() {
      if (!this.selectedNode) return {}
      return this.scoreStyle(this.selectedNode.attributeColor)
    },
    strongConnectionCount() {
      if (!this.visibleModel) return 0
      return this.visibleModel.edges.filter(edge => edge.isStrong || edge.weight >= 0.8).length
    },
    averageConnectionStrength() {
      if (!this.visibleModel || this.visibleModel.edges.length === 0) return 0
      const total = this.visibleModel.edges.reduce((sum, edge) => sum + Number(edge.weight || 0), 0)
      return Math.round((total / this.visibleModel.edges.length) * 100)
    },
    networkDensity() {
      if (!this.visibleModel || this.visibleModel.nodes.length < 2) return 0
      const possible = (this.visibleModel.nodes.length * (this.visibleModel.nodes.length - 1)) / 2
      const density = Math.min(100, (this.visibleModel.edges.length / possible) * 100)

      if (density === 0) return 0
      if (density < 0.1) return '<0.1'
      if (density < 10) return density.toFixed(1)
      return Math.round(density)
    },
    networkNotice() {
      if (!this.graphModel) return ''
      if (this.detailMode === 'detail') {
        return 'Names and close connections expanded'
      }
      if (this.detailMode === 'cluster') {
        return 'Team labels and local clusters expanded'
      }
      if (this.hiddenEdgeCount > 0) {
        return `${this.formatNumber(this.hiddenEdgeCount)} lower-strength links hidden`
      }
      if (this.graphModel.totalEdgeCount > 500) {
        return 'Layout settles briefly, then stays still'
      }
      return ''
    },
    distributionCounts() {
      const counts = {
        red: 0,
        yellow: 0,
        green: 0,
        unknown: 0,
        all: 0,
      }

      if (!this.visibleModel) return counts

      this.visibleModel.nodes.forEach(node => {
        counts.all += 1
        if (node.attributeColor === '#F44336' || node.attributeColor === '#BF3328') {
          counts.red += 1
        } else if (node.attributeColor === '#FBC02D') {
          counts.yellow += 1
        } else if (node.attributeColor === '#66BB6A' || node.attributeColor === '#3B8D3E') {
          counts.green += 1
        } else {
          counts.unknown += 1
        }
      })

      return counts
    },
    distributionTotal() {
      return this.distributionCounts.all
    },
    selectedAttributeLegend() {
      return ATTRIBUTE_LEGENDS[this.selectedAttribute] || ATTRIBUTE_LEGENDS.happiness
    },
    attributeLegendItems() {
      const legend = this.selectedAttributeLegend
      const colors = legend.colors || {}
      const labels = legend.labels || {}

      return (legend.order || ['red', 'yellow', 'green', 'unknown'])
        .map(key => ({
          key,
          label: labels[key] || key,
          color: colors[key] || ATTRIBUTE_BAND_COLORS[key] || ATTRIBUTE_BAND_COLORS.unknown,
          count: this.distributionCounts[key] || 0,
        }))
        .filter(item => item.count > 0 || item.key !== 'unknown')
    },
    distributionSegments() {
      const total = this.distributionCounts.all || 1
      return this.attributeLegendItems
        .map(item => ({
          key: item.key,
          count: item.count,
          percent: (item.count / total) * 100,
          color: item.color,
        }))
        .filter(segment => segment.count > 0)
    },
    distributionTitle() {
      return this.attributeLegendItems
        .map(item => `${item.label}: ${this.formatNumber(item.count)}`)
        .join(', ')
    },
    clusterChips() {
      if (!this.visibleModel || this.detailMode === 'overview') return []
      const counts = {}
      const nodeIds = this.viewportNodeIds.length > 0
        ? this.viewportNodeIds
        : this.visibleModel.nodes.map(node => node.id)

      nodeIds.forEach(nodeId => {
        const node = this.nodeLookup[nodeId]
        if (!node) return
        const name = node.teamName || 'No team'
        if (!counts[name]) {
          counts[name] = {
            name,
            color: node.teamColor || '#747474',
            count: 0,
            nodeIds: [],
          }
        }
        counts[name].count += 1
        counts[name].nodeIds.push(node.id)
      })

      return Object.values(counts)
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
        .slice(0, 5)
    },
  },
  watch: {
    scopedRows: {
      handler() {
        this.rebuildGraph()
      },
      deep: false,
    },
    allRows: {
      handler() {
        this.rebuildGraph()
      },
      deep: false,
    },
    selectedAttribute() {
      this.rebuildGraph({ keepThreshold: true })
    },
    linkMode() {
      this.thresholdTouched = false
      this.rebuildGraph()
    },
    nodeDisplayMode() {
      this.rebuildGraph({ keepThreshold: true })
    },
    focusedEmail() {
      this.selectedNodeId = this.focusedEmail || ''
      this.rebuildGraph({ keepThreshold: true })
    },
    blurMode() {
      this.rebuildGraph({ keepThreshold: true })
    },
    highlightedSignatures() {
      this.refreshRenderer()
      this.syncEdgeFlowAnimation()
    },
    highlightedSignatureGroups() {
      this.refreshRenderer()
      this.syncEdgeFlowAnimation()
    },
    height() {
      this.scheduleRendererStabilization()
    },
    loading(isLoading) {
      if (!isLoading) this.scheduleRendererStabilization()
    },
  },
  mounted() {
    this.rebuildGraph()
    this.resizeHandler = () => {
      this.stabilizeRenderer()
      this.scheduleViewportUpdate()
      this.scheduleSelectedNodePositionUpdate()
    }
    this.keydownHandler = event => this.handleKeydown(event)
    window.addEventListener('resize', this.resizeHandler)
    window.addEventListener('keydown', this.keydownHandler)
    this.setupMotionPreference()
    this.observeStageResize()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeHandler)
    window.removeEventListener('keydown', this.keydownHandler)
    this.teardownMotionPreference()
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    this.clearRendererStabilization()
    this.stopEdgeFlowAnimation({ clear: true })
    if (this.viewportUpdateFrame) {
      cancelAnimationFrame(this.viewportUpdateFrame)
      this.viewportUpdateFrame = null
    }
    if (this.selectedNodePositionFrame) {
      cancelAnimationFrame(this.selectedNodePositionFrame)
      this.selectedNodePositionFrame = null
    }
    this.clearHoverTimer()
    this.destroyGraph()
  },
  methods: {
    setupMotionPreference() {
      if (typeof window === 'undefined' || !window.matchMedia) return
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
      this.reducedMotion = Boolean(this.reducedMotionQuery.matches)
      this.reducedMotionHandler = event => {
        this.reducedMotion = Boolean(event.matches)
        this.syncEdgeFlowAnimation()
      }

      if (this.reducedMotionQuery.addEventListener) {
        this.reducedMotionQuery.addEventListener('change', this.reducedMotionHandler)
      } else if (this.reducedMotionQuery.addListener) {
        this.reducedMotionQuery.addListener(this.reducedMotionHandler)
      }
    },
    teardownMotionPreference() {
      if (!this.reducedMotionQuery || !this.reducedMotionHandler) return

      if (this.reducedMotionQuery.removeEventListener) {
        this.reducedMotionQuery.removeEventListener('change', this.reducedMotionHandler)
      } else if (this.reducedMotionQuery.removeListener) {
        this.reducedMotionQuery.removeListener(this.reducedMotionHandler)
      }

      this.reducedMotionQuery = null
      this.reducedMotionHandler = null
    },
    observeStageResize() {
      if (typeof window === 'undefined' || !window.ResizeObserver || !this.$refs.stage) return
      this.resizeObserver = new window.ResizeObserver(() => {
        this.stabilizeRenderer()
        this.syncEdgeFlowAnimation()
      })
      this.resizeObserver.observe(this.$refs.stage)
    },
    clearRendererStabilization() {
      if (this.rendererSettleFrame) {
        cancelAnimationFrame(this.rendererSettleFrame)
        this.rendererSettleFrame = null
      }
      this.rendererSettleTimers.forEach(timer => window.clearTimeout(timer))
      this.rendererSettleTimers = []
    },
    stabilizeRenderer() {
      if (!this.renderer || !this.$refs.stage) return false
      const rect = this.$refs.stage.getBoundingClientRect()
      if (!rect.width || !rect.height) return false

      try {
        this.renderer.resize()
        this.renderer.refresh()
        this.scheduleViewportUpdate()
        this.scheduleSelectedNodePositionUpdate()
        this.syncEdgeFlowAnimation()
        return true
      } catch (error) {
        return false
      }
    },
    settleCameraAfterRender() {
      if (!this.renderer || !this.graph || this.graph.order === 0) return
      if (this.isEgoLayout && this.visibleModel) {
        this.zoomToNodes(this.visibleModel.nodes.map(node => node.id), {
          minRatio: 0.18,
          maxRatio: 1.1,
          ratioMultiplier: 2.9,
        })
        return
      }
      const activeNodeId = this.activeNodeId
      if (activeNodeId && this.graph.hasNode(activeNodeId)) {
        this.zoomToNodeNeighborhood(activeNodeId)
      } else {
        this.resetCamera()
      }
    },
    scheduleRendererStabilization(options = {}) {
      this.clearRendererStabilization()
      const renderCycle = this.renderCycle
      let cameraSettled = false
      const run = () => {
        if (renderCycle !== this.renderCycle) return
        const stable = this.stabilizeRenderer()
        if (stable && options.settleCamera && !cameraSettled) {
          cameraSettled = true
          this.settleCameraAfterRender()
        }
      }

      this.$nextTick(() => {
        if (this._isDestroyed || renderCycle !== this.renderCycle) return
        this.rendererSettleFrame = requestAnimationFrame(() => {
          this.rendererSettleFrame = null
          run()
        })
        const delays = [80, 220, 500]
        delays.forEach(delay => {
          const timer = window.setTimeout(run, delay)
          this.rendererSettleTimers.push(timer)
        })
      })
    },
    formatNumber(value) {
      const number = Number(value || 0)
      return number.toLocaleString()
    },
    scoreStyle(color) {
      if (color === '#66BB6A' || color === '#3B8D3E') {
        return { backgroundColor: '#E7F6EC', color: '#256D32' }
      }
      if (color === '#FBC02D') {
        return { backgroundColor: '#FFF8D8', color: '#775A00' }
      }
      if (color === '#F44336' || color === '#BF3328') {
        return { backgroundColor: '#FEECEC', color: '#9F1D15' }
      }
      return { backgroundColor: '#EEF2F7', color: '#4B5563' }
    },
    normalizeSignatureKey(value) {
      return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
    },
    addSignatureLookupValue(lookup, value) {
      const normalized = this.normalizeSignatureKey(value)
      if (!normalized) return

      lookup[normalized] = true
      lookup[normalized.replace(/\s+/g, '')] = true
    },
    buildSignatureLookup(values) {
      const lookup = {}
      if (!Array.isArray(values)) return lookup

      values.forEach(value => this.addSignatureLookupValue(lookup, value))
      return lookup
    },
    nodeMatchesSignatureHighlight(node) {
      if (!node || !this.hasSignatureHighlight) return false
      const person = node.person || {}
      const groups = Array.isArray(person.signature_groups) ? person.signature_groups : []
      if (Object.keys(this.signatureGroupHighlightLookup).length > 0 && groups.length > 0) {
        return groups.some(group => {
          const values = typeof group === 'string'
            ? [group]
            : [group.key, group.name]

          return values.some(value => {
            const normalized = this.normalizeSignatureKey(value)
            return Boolean(normalized && (
              this.signatureGroupHighlightLookup[normalized] ||
              this.signatureGroupHighlightLookup[normalized.replace(/\s+/g, '')]
            ))
          })
        })
      }

      const signatures = Array.isArray(person.signatures) ? person.signatures : []
      if (signatures.length === 0) return false

      return signatures.some(signature => {
        const values = typeof signature === 'string'
          ? [signature]
          : [signature.name, signature.type, signature.id]

        return values.some(value => {
          const normalized = this.normalizeSignatureKey(value)
          return Boolean(normalized && (
            this.signatureHighlightLookup[normalized] ||
            this.signatureHighlightLookup[normalized.replace(/\s+/g, '')]
          ))
        })
      })
    },
    edgeStrengthTier(edgeData) {
      if (edgeData && edgeData.type === 'management') return 'management'
      if (edgeData && (edgeData.isStrong || Number(edgeData.weight || 0) >= 0.8)) return 'strong'
      return 'standard'
    },
    edgeDisplayStyle(edgeData = {}, options = {}) {
      const tier = this.edgeStrengthTier(edgeData)
      let style = {
        color: BRAND_PURPLE_EDGE,
        size: 0.75,
        zIndex: 1,
      }

      if (tier === 'management') {
        style = {
          color: 'rgba(75, 85, 99, 0.5)',
          size: 1.05,
          zIndex: 2,
        }
      } else if (tier === 'strong') {
        style = {
          color: BRAND_PURPLE_EDGE_STRONG,
          size: 1.85,
          zIndex: 4,
        }
      }

      if (options.mode === 'overview' && tier === 'standard') {
        style = {
          ...style,
          color: BRAND_PURPLE_EDGE_LIGHT,
          size: 0.45,
        }
      }

      if (options.muted) {
        return {
          color: 'rgba(203, 213, 225, 0.26)',
          size: tier === 'strong' ? 0.55 : 0.3,
          zIndex: 0,
        }
      }

      if (options.emphasized) {
        return {
          color: tier === 'management' ? '#374151' : BRAND_PURPLE_DARK,
          size: Math.min(2.8, style.size + 0.55),
          zIndex: style.zIndex + 6,
        }
      }

      return style
    },
    focusConnection(connection) {
      if (!connection || !connection.node) return
      this.clearHoveredNode({ immediate: true })
      this.stopLayout()
      this.selectedNodeId = connection.node.id
      this.$emit('focus-person', connection.node.person)
      this.zoomToNodeNeighborhood(connection.node.id)
      this.scheduleSelectedNodePositionUpdate()
      this.refreshRenderer()
    },
    clearHoverTimer() {
      if (!this.hoverClearTimeout) return
      window.clearTimeout(this.hoverClearTimeout)
      this.hoverClearTimeout = null
    },
    setHoveredNode(nodeId) {
      this.clearHoverTimer()
      if (this.selectedNode || this.hoveredNodeId === nodeId) return
      this.hoveredNodeId = nodeId
      this.refreshRenderer()
    },
    clearHoveredNode(options = {}) {
      this.clearHoverTimer()
      const clear = () => {
        this.hoverClearTimeout = null
        if (!this.hoveredNodeId) return
        this.hoveredNodeId = ''
        this.refreshRenderer()
      }

      if (options.immediate) {
        clear()
        return
      }

      this.hoverClearTimeout = window.setTimeout(clear, 80)
    },
    isProminentNode(node) {
      if (!node) return false
      return node.isFocused || Number(node.powerRating || 0) >= 80 || Number(node.size || 0) >= 10
    },
    isNodeInViewport(nodeId) {
      return this.viewportNodeIds.length === 0 || Boolean(this.viewportNodeLookup[nodeId])
    },
    nodeDisplaySuppressesText() {
      return this.blurMode || this.normalizedNodeDisplayMode === 'none' || this.normalizedNodeDisplayMode === 'blurred'
    },
    nodeDisplayShortLabel(node) {
      return node && node.shortLabel ? node.shortLabel : ''
    },
    nodeDisplayTeamLabel(node) {
      if (!node) return ''
      const shortLabel = this.nodeDisplayShortLabel(node)
      if (this.normalizedNodeDisplayMode === 'person_team') {
        return node.label || shortLabel
      }
      if (this.normalizedNodeDisplayMode === 'initials_team' && node.teamName) return `${shortLabel} · ${node.teamName}`
      return shortLabel
    },
    progressiveNodeLabel(node) {
      if (!node || this.nodeDisplaySuppressesText()) return ''

      const count = this.viewportNodeCount
      const prominent = this.isProminentNode(node)
      const shortLabel = this.nodeDisplayShortLabel(node)
      const teamLabel = this.nodeDisplayTeamLabel(node)

      if (count > 220) return ''
      if (count > 140) return prominent ? shortLabel : ''

      if (this.detailMode === 'overview') {
        return prominent ? shortLabel : ''
      }
      if (this.detailMode === 'cluster') {
        if (!this.isNodeInViewport(node.id) && !this.isProminentNode(node)) return ''
        if (count > 75) return prominent ? shortLabel : ''
        return teamLabel
      }
      if (count > 45 && !prominent) return shortLabel
      return teamLabel
    },
    firstNameLastInitial(name) {
      const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
      if (parts.length === 0) return ''
      if (parts.length === 1) return parts[0]

      const firstName = parts[0]
      const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase()

      return lastInitial ? `${firstName} ${lastInitial}.` : firstName
    },
    hoverNodeLabel(node) {
      if (!node || this.nodeDisplaySuppressesText()) return ''

      const personLabel = this.firstNameLastInitial(node.label) || node.shortLabel
      return node.teamName ? `${personLabel} · ${node.teamName}` : personLabel
    },
    scaledNodeSize(size, options = {}) {
      const base = Number(size || 4)
      const multiplier = Number(options.multiplier || 1)
      const bump = Number(options.bump || 0)
      const min = options.min === undefined ? 3 : options.min
      const max = options.max === undefined ? 18 : options.max
      const scaled = (base * multiplier * this.adaptiveNodeScale) + bump

      return Math.max(min, Math.min(max, scaled))
    },
    scheduleViewportUpdate() {
      if (this.viewportUpdateFrame) return
      this.viewportUpdateFrame = requestAnimationFrame(() => {
        this.viewportUpdateFrame = null
        this.updateViewportState()
      })
    },
    scheduleSelectedNodePositionUpdate() {
      if (this.selectedNodePositionFrame) return
      this.selectedNodePositionFrame = requestAnimationFrame(() => {
        this.selectedNodePositionFrame = null
        this.updateSelectedNodePosition()
      })
    },
    updateSelectedNodePosition() {
      if (!this.renderer || !this.graph || !this.selectedNode || !this.graph.hasNode(this.selectedNode.id)) {
        this.selectedNodeViewportPoint = null
        return
      }

      try {
        const attributes = this.graph.getNodeAttributes(this.selectedNode.id)
        const point = this.renderer.graphToViewport({ x: attributes.x, y: attributes.y })
        const dimensions = this.renderer.getDimensions ? this.renderer.getDimensions() : { width: 0, height: 0 }
        const padding = 80

        if (
          dimensions.width &&
          dimensions.height &&
          (
            point.x < -padding ||
            point.x > dimensions.width + padding ||
            point.y < -padding ||
            point.y > dimensions.height + padding
          )
        ) {
          this.selectedNodeViewportPoint = null
          return
        }

        this.selectedNodeViewportPoint = point
      } catch (error) {
        this.selectedNodeViewportPoint = null
      }
    },
    commitViewportState(ids) {
      const nextIds = Array.isArray(ids) ? ids : []
      this.viewportNodeIds = nextIds
      this.scheduleSelectedNodePositionUpdate()

      const nextSignature = `${this.detailMode}:${this.nodeScaleBucket}:${nextIds.join('|')}`
      if (this.viewportRenderSignature === nextSignature) return

      this.viewportRenderSignature = nextSignature
      this.refreshRenderer()
      this.syncEdgeFlowAnimation()
    },
    updateViewportState() {
      if (!this.renderer || !this.graph || !this.visibleModel) return

      const dimensions = this.renderer.getDimensions ? this.renderer.getDimensions() : { width: 0, height: 0 }
      const ids = []
      const padding = 48

      if (!dimensions.width || !dimensions.height) {
        this.commitViewportState(this.visibleModel.nodes.map(node => node.id).sort())
        return
      }

      try {
        this.graph.forEachNode((node, attributes) => {
          const point = this.renderer.graphToViewport({ x: attributes.x, y: attributes.y })
          if (
            point.x >= -padding &&
            point.x <= dimensions.width + padding &&
            point.y >= -padding &&
            point.y <= dimensions.height + padding
          ) {
            ids.push(node)
          }
        })
      } catch (error) {
        this.commitViewportState(this.visibleModel.nodes.map(item => item.id).sort())
        return
      }

      ids.sort()
      this.commitViewportState(ids)
    },
    zoomIn() {
      if (!this.renderer) return
      this.renderer.getCamera().animatedZoom({ duration: this.motionDuration(220), factor: 1.45 })
    },
    zoomOut() {
      if (!this.renderer) return
      this.renderer.getCamera().animatedUnzoom({ duration: this.motionDuration(220), factor: 1.45 })
    },
    zoomToSelectedNeighborhood() {
      if (!this.selectedNode) return
      this.zoomToNodeNeighborhood(this.selectedNode.id)
    },
    zoomToNodeNeighborhood(nodeId) {
      if (!nodeId || !this.graphModel) return
      const neighborIds = this.graphModel.edges
        .filter(edge => edge.source === nodeId || edge.target === nodeId)
        .sort((a, b) => b.weight - a.weight)
        .slice(0, 12)
        .map(edge => edge.source === nodeId ? edge.target : edge.source)
      this.zoomToNodes([nodeId, ...neighborIds])
    },
    zoomToNodes(nodeIds, options = {}) {
      if (!this.renderer || !this.graph || !Array.isArray(nodeIds) || nodeIds.length === 0) return

      const points = []
      nodeIds.forEach(nodeId => {
        if (!this.graph.hasNode(nodeId)) return
        const attributes = this.graph.getNodeAttributes(nodeId)
        try {
          const viewportPoint = this.renderer.graphToViewport({ x: attributes.x, y: attributes.y })
          points.push(this.renderer.viewportToFramedGraph(viewportPoint))
        } catch (error) {
          /* Ignore nodes while renderer is between refreshes. */
        }
      })

      if (points.length === 0) return

      const xs = points.map(point => point.x)
      const ys = points.map(point => point.y)
      const minX = Math.min(...xs)
      const maxX = Math.max(...xs)
      const minY = Math.min(...ys)
      const maxY = Math.max(...ys)
      const span = Math.max(maxX - minX, maxY - minY, points.length === 1 ? 0.08 : 0.16)
      const minRatio = options.minRatio === undefined ? 0.08 : options.minRatio
      const maxRatio = options.maxRatio === undefined ? 0.9 : options.maxRatio
      const ratioMultiplier = options.ratioMultiplier === undefined ? 2.2 : options.ratioMultiplier
      const targetRatio = Math.max(minRatio, Math.min(maxRatio, span * ratioMultiplier))
      const camera = this.renderer.getCamera()

      camera.animate({
        x: (minX + maxX) / 2,
        y: (minY + maxY) / 2,
        ratio: camera.getBoundedRatio(targetRatio),
      }, { duration: this.motionDuration(360) })
    },
    motionDuration(duration) {
      if (typeof window === 'undefined' || !window.matchMedia) return duration

      try {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : duration
      } catch (error) {
        return duration
      }
    },
    shouldAnimateEdgeFlow() {
      if (
        this.reducedMotion ||
        this.isLoading ||
        this.renderError ||
        !this.renderer ||
        !this.graph ||
        !this.visibleModel ||
        this.visibleModel.edges.length === 0
      ) return false

      if (this.detailMode === 'focus') return true
      return this.detailMode === 'detail' && this.viewportNodeCount <= 70
    },
    syncEdgeFlowAnimation() {
      if (this.shouldAnimateEdgeFlow()) {
        this.startEdgeFlowAnimation()
      } else {
        this.stopEdgeFlowAnimation({ clear: true })
      }
    },
    startEdgeFlowAnimation() {
      if (this.edgeFlowFrame || typeof requestAnimationFrame === 'undefined') return
      this.edgeFlowStartedAt = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now()

      const render = now => {
        this.edgeFlowFrame = null
        if (!this.shouldAnimateEdgeFlow()) {
          this.stopEdgeFlowAnimation({ clear: true })
          return
        }

        this.drawEdgeFlowFrame(now)
        this.edgeFlowFrame = requestAnimationFrame(render)
      }

      this.edgeFlowFrame = requestAnimationFrame(render)
    },
    stopEdgeFlowAnimation(options = {}) {
      if (this.edgeFlowFrame) {
        cancelAnimationFrame(this.edgeFlowFrame)
        this.edgeFlowFrame = null
      }

      if (options.clear) this.clearEdgeFlowCanvas()
    },
    resizeStageCanvas(canvas) {
      const stage = this.$refs.stage
      if (!stage || !canvas) return false
      const rect = stage.getBoundingClientRect()
      if (!rect.width || !rect.height) return false

      const ratio = typeof window === 'undefined' ? 1 : Math.max(1, Math.min(window.devicePixelRatio || 1, 2))
      const width = Math.round(rect.width * ratio)
      const height = Math.round(rect.height * ratio)
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width
        canvas.height = height
        canvas.style.width = `${Math.round(rect.width)}px`
        canvas.style.height = `${Math.round(rect.height)}px`
      }

      return ratio
    },
    clearEdgeFlowCanvas() {
      const canvas = this.$refs.flowCanvas
      if (!canvas) return
      const context = canvas.getContext('2d')
      if (!context) return
      context.clearRect(0, 0, canvas.width || 0, canvas.height || 0)
    },
    resizeEdgeFlowCanvas(canvas) {
      const ratio = this.resizeStageCanvas(canvas)
      if (!ratio) return false

      if (canvas.width !== this.edgeFlowCanvasWidth || canvas.height !== this.edgeFlowCanvasHeight) {
        this.edgeFlowCanvasWidth = canvas.width
        this.edgeFlowCanvasHeight = canvas.height
      }

      return ratio
    },
    edgeFlowColor(edgeData) {
      if (edgeData.type === 'management') return 'rgba(75, 85, 99, 0.62)'
      if (edgeData.isStrong || Number(edgeData.weight || 0) >= STRONG_LINK_THRESHOLD) return 'rgba(108, 48, 204, 0.72)'
      return 'rgba(156, 111, 228, 0.5)'
    },
    edgeFlowPattern(edgeData) {
      const tier = this.edgeStrengthTier(edgeData)
      return EDGE_FLOW_PATTERNS[tier] || EDGE_FLOW_PATTERNS.standard
    },
    edgeFlowKeys() {
      if (!this.graph) return []
      const activeNodeId = this.activeNodeId
      const hoverNodeId = activeNodeId ? '' : this.hoveredNodeId
      const keys = []

      this.graph.forEachEdge((edge, attributes, source, target) => {
        const edgeData = attributes.edgeData || {}
        const touchesActive = activeNodeId && (source === activeNodeId || target === activeNodeId)
        const touchesHover = hoverNodeId && (source === hoverNodeId || target === hoverNodeId)
        const touchesSignatureHighlight = this.hasSignatureHighlight && (
          this.signatureHighlightedNodeIds[source] ||
          this.signatureHighlightedNodeIds[target]
        )
        const inViewport = this.isNodeInViewport(source) || this.isNodeInViewport(target)

        if (activeNodeId && !touchesActive) return
        if (!activeNodeId && hoverNodeId && !touchesHover) return
        if (!activeNodeId && !hoverNodeId && this.hasSignatureHighlight && !touchesSignatureHighlight) return
        if (!activeNodeId && !hoverNodeId && !inViewport) return

        keys.push({
          edge,
          source,
          target,
          edgeData,
          weight: Number(edgeData.weight || attributes.weight || 0),
          priority: touchesActive || touchesHover ? 2 : touchesSignatureHighlight ? 1.5 : edgeData.isStrong ? 1 : 0,
        })
      })

      return keys
        .sort((a, b) => b.priority - a.priority || b.weight - a.weight || a.edge.localeCompare(b.edge))
        .slice(0, activeNodeId || hoverNodeId ? 64 : EDGE_FLOW_MAX_EDGES)
    },
    drawFlowPulse(context, start, end, progress, color, pattern, options = {}) {
      const safeProgress = ((progress % 1) + 1) % 1
      const dx = end.x - start.x
      const dy = end.y - start.y
      const length = Math.max(1, Math.sqrt(dx * dx + dy * dy))
      const unitX = dx / length
      const unitY = dy / length
      const scale = options.scale || 1
      const trackOffset = options.trackOffset || 0
      const perpendicularX = -unitY * trackOffset
      const perpendicularY = unitX * trackOffset
      const trim = Math.min(Math.max(pattern.nodeGap * scale, 5), length * 0.22)
      const flowLength = Math.max(1, length - trim * 2)
      const from = {
        x: start.x + unitX * trim + perpendicularX,
        y: start.y + unitY * trim + perpendicularY,
      }
      const to = {
        x: end.x - unitX * trim + perpendicularX,
        y: end.y - unitY * trim + perpendicularY,
      }
      const easedProgress = easeInOutSine(safeProgress)
      const x = from.x + (to.x - from.x) * easedProgress
      const y = from.y + (to.y - from.y) * easedProgress
      const tail = Math.min(pattern.trail * scale, flowLength * 0.26)
      const alpha = pulseEnvelope(safeProgress)
      const arrivalProgress = safeProgress > 0.76 ? (safeProgress - 0.76) / 0.24 : 0
      const arrivalAlpha = arrivalProgress > 0 ? pulseEnvelope(arrivalProgress) : 0

      context.save()
      context.globalAlpha = pattern.alpha * (0.22 + alpha * 0.78)
      context.strokeStyle = color
      context.lineWidth = Math.max(pattern.lineWidth * scale, 1)
      context.lineCap = 'round'
      context.beginPath()
      context.moveTo(x - unitX * tail, y - unitY * tail)
      context.lineTo(x, y)
      context.stroke()

      context.globalAlpha = pattern.alpha * (0.38 + alpha * 0.62)
      context.fillStyle = color
      context.beginPath()
      context.arc(x, y, Math.max(pattern.radius * scale, 1.35), 0, Math.PI * 2)
      context.fill()

      if (arrivalAlpha > 0) {
        context.globalAlpha = pattern.arrivalAlpha * arrivalAlpha
        context.strokeStyle = color
        context.lineWidth = Math.max(1 * scale, 0.8)
        context.beginPath()
        context.arc(
          to.x,
          to.y,
          Math.max(pattern.arrivalRadius * scale * (0.75 + arrivalAlpha * 0.65), 2),
          0,
          Math.PI * 2,
        )
        context.stroke()
      }

      context.restore()
    },
    drawEdgeFlowFrame(now) {
      const canvas = this.$refs.flowCanvas
      if (!canvas || !this.renderer || !this.graph) return
      const pixelRatio = this.resizeEdgeFlowCanvas(canvas)
      if (!pixelRatio) return

      const context = canvas.getContext('2d')
      if (!context) return
      const width = canvas.width
      const height = canvas.height
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
      context.clearRect(0, 0, width / pixelRatio, height / pixelRatio)

      const elapsed = ((now || 0) - this.edgeFlowStartedAt) / 1000
      const edges = this.edgeFlowKeys()
      edges.forEach((item, index) => {
        if (!this.graph.hasNode(item.source) || !this.graph.hasNode(item.target)) return
        const sourceAttributes = this.graph.getNodeAttributes(item.source)
        const targetAttributes = this.graph.getNodeAttributes(item.target)
        const sourcePoint = this.renderer.graphToViewport({ x: sourceAttributes.x, y: sourceAttributes.y })
        const targetPoint = this.renderer.graphToViewport({ x: targetAttributes.x, y: targetAttributes.y })
        const padding = 40
        const maxWidth = width / pixelRatio
        const maxHeight = height / pixelRatio

        if (
          (sourcePoint.x < -padding && targetPoint.x < -padding) ||
          (sourcePoint.x > maxWidth + padding && targetPoint.x > maxWidth + padding) ||
          (sourcePoint.y < -padding && targetPoint.y < -padding) ||
          (sourcePoint.y > maxHeight + padding && targetPoint.y > maxHeight + padding)
        ) return

        const color = this.edgeFlowColor(item.edgeData)
        const pattern = this.edgeFlowPattern(item.edgeData)
        const cadenceVariance = 0.9 + stableUnitValue(item.edge, 'cadence') * 0.2
        const phaseVariance = stableUnitValue(item.edge, 'phase') * 0.72
        const duration = pattern.cycleDuration * cadenceVariance
        const progress = ((elapsed / duration) + phaseVariance + (index * 0.031)) % 1
        const weightScale = clamp(0.88 + item.weight * 0.28, 0.88, 1.18)
        const focusScale = item.priority >= 2 ? 1.08 : 1
        const scale = weightScale * focusScale
        const twoWayOffset = item.edgeData.direction === 'two_way' ? Math.min(4.5, Math.max(2.2, 2.8 * scale)) : 0

        this.drawFlowPulse(context, sourcePoint, targetPoint, progress, color, pattern, {
          scale,
          trackOffset: twoWayOffset,
        })
        if (item.edgeData.direction === 'two_way') {
          const returnProgress = (progress + pattern.returnOffset + stableUnitValue(item.edge, 'return') * 0.08) % 1
          this.drawFlowPulse(context, targetPoint, sourcePoint, returnProgress, color, pattern, {
            scale: scale * 0.92,
            trackOffset: -twoWayOffset,
          })
        }
      })
    },
    defaultThreshold(model) {
      if (this.isManagementLinkMode || this.isMeaningfulLinkMode) return 0
      if (!model) return 0
      const hasStrongLinks = model.edges.some(edge => edge.isStrong || Number(edge.weight || 0) >= STRONG_LINK_THRESHOLD)
      if (model.totalEdgeCount > 500 && hasStrongLinks) return STRONG_LINK_THRESHOLD
      return 0
    },
    rebuildGraph(options = {}) {
      this.renderError = ''
      this.clearHoveredNode({ immediate: true })
      this.cacheCurrentPositions()

      try {
        this.graphModel = buildRelationshipGraphModel({
          scopedRows: this.scopedRows,
          allRows: this.allRows,
          linkMode: this.normalizedLinkMode,
          selectedAttribute: this.selectedAttribute,
          focusedEmail: this.focusedEmail,
          layoutMode: this.normalizedLayoutMode,
          maxEdges: this.maxEdges > 0 ? this.maxEdges : (this.fullscreen ? 2600 : 1800),
          includeConnectedOutsideScope: this.includeConnectedOutsideScope,
        })

        if (!options.keepThreshold && !this.thresholdTouched) {
          this.edgeThreshold = this.defaultThreshold(this.graphModel)
        }

        this.visibleModel = filterRelationshipGraphModel(this.graphModel, this.edgeThreshold, this.focusedEmail)
        this.renderSigma()
      } catch (error) {
        this.renderError = 'The graph data could not be prepared.'
        // eslint-disable-next-line no-console
        console.error('[RelationshipNetworkMap] render failed', error)
        this.destroyGraph()
      }
    },
    applyEdgeThreshold() {
      this.thresholdTouched = true
      if (!this.graphModel) return
      this.visibleModel = filterRelationshipGraphModel(this.graphModel, this.edgeThreshold, this.focusedEmail)
      this.renderSigma()
    },
    setLinkFilter(option) {
      if (!option || option.disabled || option.key === this.activeLinkFilterKey) return
      this.edgeThreshold = option.threshold
      this.applyEdgeThreshold()
    },
    cacheCurrentPositions() {
      if (this.isEgoLayout) {
        this.positionCache = {}
        return
      }
      if (!this.graph) return
      const cache = { ...this.positionCache }
      this.graph.forEachNode((node, attributes) => {
        cache[node] = { x: attributes.x, y: attributes.y }
      })
      this.positionCache = cache
    },
    destroyGraph() {
      if (this.selectedNodePositionFrame) {
        cancelAnimationFrame(this.selectedNodePositionFrame)
        this.selectedNodePositionFrame = null
      }
      this.stopLayout({ updateViewport: false, kill: true })
      if (this.renderer) {
        if (this.cameraUpdatedHandler) {
          try {
            this.renderer.getCamera().off('updated', this.cameraUpdatedHandler)
          } catch (e) {
            /* camera already gone */
          }
          this.cameraUpdatedHandler = null
        }
        this.renderer.kill()
        this.renderer = null
      }
      this.graph = null
      this.selectedNodeViewportPoint = null
      this.stopEdgeFlowAnimation({ clear: true })
    },
    stopLayout(options = {}) {
      if (this.layoutStopTimer) {
        window.clearTimeout(this.layoutStopTimer)
        this.layoutStopTimer = null
      }

      if (!this.layout) return

      try {
        if (options.kill && this.layout.kill) {
          this.layout.kill()
        } else {
          this.layout.stop()
        }
      } catch (e) {
        /* layout already stopped */
      }

      this.layout = null

      if (options.updateViewport !== false) {
        this.scheduleViewportUpdate()
        this.scheduleSelectedNodePositionUpdate()
      }
    },
    fitCanvasText(context, text, maxWidth) {
      const value = String(text || '')
      if (!value || maxWidth <= 0) return ''
      if (context.measureText(value).width <= maxWidth) return value

      const suffix = '...'
      let low = 0
      let high = value.length

      while (low < high) {
        const mid = Math.ceil((low + high) / 2)
        const candidate = `${value.slice(0, mid).trim()}${suffix}`
        if (context.measureText(candidate).width <= maxWidth) {
          low = mid
        } else {
          high = mid - 1
        }
      }

      const trimmed = value.slice(0, low).trim()
      return trimmed ? `${trimmed}${suffix}` : ''
    },
    drawLabelText(context, text, x, y, options = {}) {
      if (!text) return 0

      context.font = options.font || '700 12px Lato, Arial, sans-serif'
      context.textBaseline = 'middle'
      context.lineJoin = 'round'

      const fittedText = this.fitCanvasText(context, text, options.maxWidth || 180)
      if (!fittedText) return 0

      context.lineWidth = options.haloWidth || 4
      context.strokeStyle = options.haloColor || 'rgba(255, 255, 255, 0.92)'
      context.strokeText(fittedText, x, y)
      context.fillStyle = options.color || '#273244'
      context.fillText(fittedText, x, y)

      return context.measureText(fittedText).width
    },
    drawNodeLabel(context, data) {
      if (!data || !data.label) return

      const mode = data.labelMode || this.normalizedNodeDisplayMode
      const teamName = data.labelTeamName || (data.nodeData && data.nodeData.teamName) || ''
      const primaryLabel = data.labelPersonName || (
        mode === 'initials_team'
          ? data.labelInitials || data.label
          : data.label
      )
      const x = data.x + data.size + 6
      const y = data.y + 0.5
      const maxWidth = this.viewportNodeCount > 75 ? 145 : (this.fullscreen ? 230 : 190)

      context.save()

      if ((mode === 'initials_team' || mode === 'person_team') && teamName) {
        const primaryMaxWidth = mode === 'person_team'
          ? Math.min(112, Math.max(70, maxWidth * 0.54))
          : 42
        const primaryWidth = this.drawLabelText(context, primaryLabel, x, y, {
          font: '800 12px Lato, Arial, sans-serif',
          color: '#273244',
          maxWidth: primaryMaxWidth,
        })
        const separatorX = x + primaryWidth + 6
        const separatorWidth = this.drawLabelText(context, '·', separatorX, y, {
          font: '800 12px Lato, Arial, sans-serif',
          color: '#9AA4B2',
          maxWidth: 10,
          haloWidth: 3,
        })
        this.drawLabelText(context, teamName, separatorX + separatorWidth + 6, y + 0.5, {
          font: '600 11px Lato, Arial, sans-serif',
          color: '#64748B',
          maxWidth: Math.max(56, maxWidth - primaryWidth - separatorWidth - 16),
          haloWidth: 3,
        })
      } else {
        this.drawLabelText(context, primaryLabel, x, y, {
          font: '800 12px Lato, Arial, sans-serif',
          color: '#273244',
          maxWidth,
        })
      }

      context.restore()
    },
    nodeLabel(node) {
      if (!node || this.nodeDisplaySuppressesText()) return ''
      return this.nodeDisplayTeamLabel(node)
    },
    fallbackNodeLabel(node) {
      if (!node || this.nodeDisplaySuppressesText()) return ''
      return this.nodeDisplayShortLabel(node)
    },
    focusedNodeLabel(node) {
      if (!node || this.nodeDisplaySuppressesText()) return ''
      return this.nodeDisplayTeamLabel(node)
    },
    nodeColor(node) {
      // Team can appear in labels and chips, but node color must always reflect the selected attribute.
      return node.attributeColor || DEFAULT_NODE_COLOR
    },
    nodeType(node) {
      return 'circle'
    },
    nodeImage(node) {
      return ''
    },
    renderSigma() {
      const stage = this.$refs.stage
      if (!stage || !this.visibleModel) return

      this.renderCycle += 1
      this.destroyGraph()
      this.viewportRenderSignature = ''

      const graph = new Graph({ multi: false, type: 'mixed' })
      this.visibleModel.nodes.forEach(node => {
        const cachedPosition = this.isEgoLayout ? null : this.positionCache[node.id]
        const type = this.nodeType(node)
        const label = this.nodeLabel(node)
        const forceLabel = Boolean(
          label &&
          (node.isFocused || this.visibleModel.nodes.length <= 35)
        )
        graph.addNode(node.id, {
          x: cachedPosition ? cachedPosition.x : node.x,
          y: cachedPosition ? cachedPosition.y : node.y,
          size: node.isFocused ? node.size + 4 : node.size,
          type,
          image: this.nodeImage(node),
          color: this.nodeColor(node),
          label,
          labelMode: this.normalizedNodeDisplayMode,
          labelInitials: node.shortLabel || '',
          labelTeamName: node.teamName || '',
          labelPersonName: '',
          nodeData: node,
          forceLabel,
          zIndex: node.isFocused ? 10 : node.isScoped ? 3 : 1,
        })
      })

      this.visibleModel.edges.forEach(edge => {
        if (!graph.hasNode(edge.source) || !graph.hasNode(edge.target)) return
        const edgeStyle = this.edgeDisplayStyle(edge)
        graph.addEdgeWithKey(edge.id, edge.source, edge.target, {
          size: edgeStyle.size,
          color: edgeStyle.color || EDGE_COLORS[edge.type] || EDGE_COLORS.relationship,
          weight: edge.weight,
          edgeData: edge,
          zIndex: edge.isFocused ? 5 : edgeStyle.zIndex,
        })
      })

      this.graph = graph

      this.renderer = new Sigma(graph, stage, {
        allowInvalidContainer: true,
        renderLabels: true,
        labelSize: 10,
        labelWeight: '600',
        labelColor: { color: '#2D3748' },
        defaultEdgeColor: BRAND_PURPLE_EDGE_DEFAULT,
        minEdgeThickness: 0.5,
        hideEdgesOnMove: graph.size > 700,
        hideLabelsOnMove: graph.order > 120,
        labelRenderedSizeThreshold: graph.order > 120 ? 11 : 8,
        defaultDrawNodeLabel: (context, data) => this.drawNodeLabel(context, data),
        zoomToSizeRatioFunction: ratio => Math.pow(Math.max(ratio, 0.08), 0.12),
        zIndex: true,
        nodeReducer: (node, data) => this.reduceNode(node, data),
        edgeReducer: (edge, data) => this.reduceEdge(edge, data),
      })

      const camera = this.renderer.getCamera()
      this.cameraRatio = camera.getState().ratio
      this.cameraUpdatedHandler = state => {
        this.cameraRatio = state.ratio
        this.scheduleViewportUpdate()
        this.scheduleSelectedNodePositionUpdate()
        this.syncEdgeFlowAnimation()
      }
      camera.on('updated', this.cameraUpdatedHandler)

      this.renderer.on('clickNode', ({ node }) => {
        this.clearHoveredNode({ immediate: true })
        this.stopLayout()
        const nodeData = this.graph && this.graph.getNodeAttribute(node, 'nodeData')
        if (this.lockFocusedNode && this.focusedEmail) {
          if (nodeData && node !== this.focusedEmail) this.$emit('preview-person', nodeData.person)
          this.zoomToNodeNeighborhood(this.focusedEmail)
          this.scheduleSelectedNodePositionUpdate()
          this.refreshRenderer()
          return
        }
        this.selectedNodeId = node
        if (nodeData) this.$emit('focus-person', nodeData.person)
        this.zoomToNodeNeighborhood(node)
        this.scheduleSelectedNodePositionUpdate()
        this.refreshRenderer()
      })
      this.renderer.on('doubleClickNode', payload => {
        if (payload.preventSigmaDefault) payload.preventSigmaDefault()
        this.clearHoveredNode({ immediate: true })
        this.zoomToNodeNeighborhood(this.lockFocusedNode && this.focusedEmail ? this.focusedEmail : payload.node)
      })
      this.renderer.on('enterNode', ({ node }) => {
        this.setHoveredNode(node)
      })
      this.renderer.on('leaveNode', () => {
        this.clearHoveredNode()
      })
      this.renderer.on('clickStage', () => {
        this.clearHoveredNode({ immediate: true })
        if (!this.focusedEmail) this.selectedNodeId = ''
        this.refreshRenderer()
      })

      this.startLayout()
      this.scheduleRendererStabilization({ settleCamera: true })
      this.syncEdgeFlowAnimation()
    },
    startLayout() {
      // Keep focus mode still; live physics makes the selected person appear to shake.
      if (
        !this.graph ||
        this.activeNodeId ||
        this.isEgoLayout ||
        this.graph.order < 4 ||
        this.graph.order > 650 ||
        this.isManagementLinkMode
      ) return
      try {
        this.layout = new FA2Layout(this.graph, {
          settings: {
            barnesHutOptimize: this.graph.order > 120,
            gravity: 1.4,
            scalingRatio: this.graph.order > 160 ? 6 : 3,
            slowDown: 8,
          },
        })
        this.layout.start()
        this.layoutStopTimer = window.setTimeout(() => {
          this.stopLayout()
        }, this.graph.order > 220 ? 3000 : 1800)
      } catch (error) {
        if (this.layoutStopTimer) {
          window.clearTimeout(this.layoutStopTimer)
          this.layoutStopTimer = null
        }
        this.layout = null
      }
    },
    reduceNode(node, data) {
      const activeNodeId = this.activeNodeId
      const hoverNodeId = activeNodeId ? '' : this.hoveredNodeId
      const nodeData = data.nodeData
      if (!activeNodeId) {
        const inViewport = this.isNodeInViewport(node)
        const prominent = this.isProminentNode(nodeData)
        const label = this.progressiveNodeLabel(nodeData)
        const isHovered = hoverNodeId === node
        const isHoverNeighbor = hoverNodeId && this.graph && (
          this.graph.hasEdge(node, hoverNodeId) ||
          this.graph.hasEdge(hoverNodeId, node)
        )
        const signatureHighlightActive = this.hasSignatureHighlight
        const signatureHighlighted = Boolean(this.signatureHighlightedNodeIds[node])

        if (isHovered) {
          const hoverLabel = this.hoverNodeLabel(nodeData)
          const fallbackLabel = this.fallbackNodeLabel(nodeData)
          const displayLabel = nodeData ? hoverLabel || label || fallbackLabel : data.label

          return {
            ...data,
            size: this.scaledNodeSize(data.size, { multiplier: 1, bump: 1.5, max: 15 }),
            label: displayLabel,
            labelMode: nodeData && nodeData.teamName ? 'person_team' : this.normalizedNodeDisplayMode,
            labelPersonName: nodeData ? this.firstNameLastInitial(nodeData.label) || nodeData.shortLabel : '',
            labelTeamName: nodeData ? nodeData.teamName || '' : '',
            highlighted: true,
            forceLabel: Boolean(displayLabel),
            zIndex: 18,
          }
        }

        if (isHoverNeighbor) {
          return {
            ...data,
            label: this.viewportNodeCount <= 75 ? label : '',
            forceLabel: Boolean(label && this.viewportNodeCount <= 75),
            size: this.scaledNodeSize(data.size, { multiplier: 0.86, bump: 0.5, max: 13 }),
            zIndex: data.zIndex + 4,
          }
        }

        if (signatureHighlightActive) {
          if (!signatureHighlighted) {
            return {
              ...data,
              color: '#CBD5E1',
              label: '',
              forceLabel: false,
              size: this.scaledNodeSize(data.size, { multiplier: 0.46, min: 2.3, max: 7 }),
              zIndex: 0,
            }
          }

          const displayLabel = this.viewportNodeCount <= 95 || prominent
            ? label || this.fallbackNodeLabel(nodeData)
            : ''

          return {
            ...data,
            label: displayLabel,
            forceLabel: Boolean(displayLabel && (inViewport || prominent)),
            size: this.scaledNodeSize(data.size, { multiplier: inViewport ? 0.98 : 0.84, bump: 0.4, max: 13 }),
            zIndex: data.zIndex + 7,
          }
        }

        if (this.detailMode === 'overview' && !prominent) {
          return {
            ...data,
            label: '',
            forceLabel: false,
            size: this.scaledNodeSize(data.size, { multiplier: 0.7, min: 2.6, max: 8 }),
          }
        }

        if (this.detailMode === 'cluster') {
          return {
            ...data,
            label,
            forceLabel: Boolean(label && inViewport),
            size: inViewport
              ? this.scaledNodeSize(data.size, { multiplier: 0.92, max: 14 })
              : this.scaledNodeSize(data.size, { multiplier: 0.58, min: 2.6, max: 8 }),
            zIndex: inViewport ? data.zIndex + 3 : data.zIndex,
          }
        }

        if (this.detailMode === 'detail') {
          return {
            ...data,
            label,
            forceLabel: Boolean(label && inViewport),
            size: inViewport
              ? this.scaledNodeSize(data.size, { multiplier: 0.98, max: 16 })
              : this.scaledNodeSize(data.size, { multiplier: 0.46, min: 2.4, max: 7 }),
            color: inViewport ? data.color : '#CBD5E1',
            zIndex: inViewport ? data.zIndex + 4 : 0,
          }
        }

        return {
          ...data,
          label,
          forceLabel: Boolean(label && prominent),
          size: this.scaledNodeSize(data.size, { max: prominent ? 14 : 11 }),
        }
      }

      if (node === activeNodeId) {
        const displayLabel = this.isEgoLayout ? '' : (nodeData ? this.focusedNodeLabel(nodeData) : data.label)
        return {
          ...data,
          size: this.scaledNodeSize(data.size, { multiplier: 0.74, bump: 1, max: 13 }),
          color: nodeData ? this.nodeColor(nodeData) : data.color,
          label: displayLabel,
          highlighted: true,
          forceLabel: Boolean(displayLabel),
          zIndex: 20,
        }
      }

      const isNeighbor = this.graph && (
        this.graph.hasEdge(node, activeNodeId) ||
        this.graph.hasEdge(activeNodeId, node)
      )
      if (isNeighbor) {
        const displayLabel = nodeData ? this.progressiveNodeLabel(nodeData) || this.fallbackNodeLabel(nodeData) : data.label
        return {
          ...data,
          size: this.scaledNodeSize(data.size, { multiplier: 0.94, bump: 1, max: 16 }),
          label: displayLabel,
          labelMode: this.normalizedNodeDisplayMode,
          labelPersonName: this.normalizedNodeDisplayMode === 'person_team' && nodeData
            ? nodeData.label || this.firstNameLastInitial(nodeData.label)
            : '',
          labelTeamName: nodeData ? nodeData.teamName || '' : '',
          forceLabel: Boolean(displayLabel),
          zIndex: 12,
        }
      }

      return {
        ...data,
        color: '#D1D5DB',
        label: '',
        size: this.scaledNodeSize(data.size, { multiplier: 0.42, min: 2.2, max: 6 }),
        zIndex: 0,
      }
    },
    reduceEdge(edge, data) {
      const activeNodeId = this.activeNodeId
      const hoverNodeId = activeNodeId ? '' : this.hoveredNodeId
      if (!this.graph) return data

      const source = this.graph.source(edge)
      const target = this.graph.target(edge)
      const edgeData = data.edgeData || {}

      if (!activeNodeId) {
        const edgeInViewport = this.isNodeInViewport(source) || this.isNodeInViewport(target)
        let displayData = data

        if (this.detailMode === 'overview') {
          const overviewStyle = this.edgeDisplayStyle(edgeData, { mode: 'overview' })
          displayData = {
            ...data,
            ...overviewStyle,
          }
        } else if (this.detailMode === 'cluster') {
          const clusterStyle = this.edgeDisplayStyle(edgeData, { muted: !edgeInViewport })
          displayData = {
            ...data,
            ...clusterStyle,
            size: edgeInViewport ? clusterStyle.size + 0.15 : clusterStyle.size,
            zIndex: edgeInViewport ? clusterStyle.zIndex + 1 : clusterStyle.zIndex,
          }
        } else if (this.detailMode === 'detail') {
          const detailStyle = this.edgeDisplayStyle(edgeData, { muted: !edgeInViewport })
          displayData = {
            ...data,
            ...detailStyle,
            size: edgeInViewport ? detailStyle.size + 0.25 : detailStyle.size,
            zIndex: edgeInViewport ? detailStyle.zIndex + 2 : detailStyle.zIndex,
          }
        }

        if (hoverNodeId && (source === hoverNodeId || target === hoverNodeId)) {
          const hoverStyle = this.edgeDisplayStyle(edgeData, { emphasized: true })
          return {
            ...displayData,
            ...hoverStyle,
          }
        }

        if (this.hasSignatureHighlight) {
          const touchesSignatureHighlight = Boolean(
            this.signatureHighlightedNodeIds[source] ||
            this.signatureHighlightedNodeIds[target]
          )
          const signatureStyle = this.edgeDisplayStyle(edgeData, {
            emphasized: touchesSignatureHighlight,
            muted: !touchesSignatureHighlight,
          })

          return {
            ...displayData,
            ...signatureStyle,
            size: touchesSignatureHighlight ? Math.min(2.5, signatureStyle.size) : signatureStyle.size,
            zIndex: touchesSignatureHighlight ? signatureStyle.zIndex + 1 : 0,
          }
        }

        return displayData
      }

      const isActive = source === activeNodeId || target === activeNodeId
      if (this.isEgoLayout) {
        const tier = this.edgeStrengthTier(edgeData)
        return {
          ...data,
          color: isActive
            ? (tier === 'strong' ? 'rgba(108, 48, 204, 0.7)' : 'rgba(108, 48, 204, 0.44)')
            : (tier === 'strong' ? 'rgba(108, 48, 204, 0.22)' : 'rgba(108, 48, 204, 0.12)'),
          size: isActive
            ? (tier === 'strong' ? 1.65 : 1)
            : (tier === 'strong' ? 0.85 : 0.45),
          zIndex: isActive ? 10 : 2,
        }
      }

      const focusStyle = this.edgeDisplayStyle(edgeData, { emphasized: isActive, muted: !isActive })

      return {
        ...data,
        ...focusStyle,
        zIndex: isActive ? 10 : focusStyle.zIndex,
      }
    },
    refreshRenderer() {
      if (this.renderer) this.renderer.refresh()
    },
    handleKeydown(event) {
      if (!event || (event.key !== 'Escape' && event.key !== 'Esc')) return
      if (!this.focusedEmail && !this.selectedNodeId && !this.fullscreen) return

      event.preventDefault()
      if (event.stopPropagation) event.stopPropagation()
      if (event.stopImmediatePropagation) event.stopImmediatePropagation()

      if (this.focusedEmail || this.selectedNodeId) {
        this.clearFocus()
        return
      }

      if (this.fullscreen) {
        this.$emit('exit-fullscreen')
      }
    },
    resetCamera() {
      if (!this.renderer) return
      const camera = this.renderer.getCamera()
      camera.animatedReset({ duration: this.motionDuration(250) }).then(() => this.scheduleViewportUpdate())
    },
    clearFocus() {
      this.selectedNodeId = ''
      this.selectedNodeViewportPoint = null
      this.clearHoveredNode({ immediate: true })
      this.$emit('clear-focus')
      this.refreshRenderer()
    },
  },
}
</script>

<style lang="scss" scoped>
.relationship-network-map {
  --relationship-brand: #6C30CC;
  --relationship-brand-dark: #4E2494;
  --relationship-brand-light: #9C6FE4;
  --relationship-brand-soft: #F4EEFF;
  --relationship-brand-faint: #FAF7FF;
  --relationship-brand-line: #D8C7F7;
  --relationship-brand-shadow: rgba(108, 48, 204, 0.24);
  --relationship-brand-edge: rgba(108, 48, 204, 0.32);
  --map-surface: #FBFCFE;
  --map-grid-color: rgba(100, 116, 139, 0.018);
  --map-grid-size: 52px;
  width: 100%;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  background: var(--map-surface);
  overflow: hidden;
}

.relationship-network-map.fullscreen {
  --map-grid-color: rgba(100, 116, 139, 0.022);
  --map-grid-size: 48px;
  height: 100%;
  border: none;
  border-radius: 0;
}

.relationship-network-map.compact {
  --map-grid-color: rgba(100, 116, 139, 0.012);
  --map-grid-size: 56px;
}

.relationship-network-map.has-signature-highlight {
  --map-surface: #FCFAFF;
  --map-grid-color: rgba(108, 48, 204, 0.014);
  --map-grid-size: 54px;
}

.relationship-network-map.has-selection,
.relationship-network-map.detail-focus {
  --map-surface: #FCFCFF;
  --map-grid-color: rgba(108, 48, 204, 0.01);
  --map-grid-size: 58px;
}

.relationship-network-map.detail-person:not(.has-selection) {
  --map-grid-color: rgba(100, 116, 139, 0.014);
  --map-grid-size: 56px;
}

.relationship-network-map.compact .map-hud {
  top: 12px;
  left: 12px;
  width: min(292px, calc(100% - 24px));
  gap: 6px;
}

.relationship-network-map.compact .network-notice,
.relationship-network-map.compact .signature-highlight-card,
.relationship-network-map.compact .detail-mode-card {
  padding: 7px 9px;
  font-size: 11px;
  line-height: 15px;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.07);
}

.relationship-network-map.compact .node-panel {
  top: 12px;
  right: 12px;
  width: 284px;
  max-width: min(284px, calc(100% - 24px));
  padding: 12px;
}

.relationship-network-map.compact .node-avatar {
  width: 36px;
  height: 36px;
  font-size: 12px;
}

.relationship-network-map.compact .node-name {
  font-size: 13px;
  line-height: 17px;
}

.relationship-network-map.compact .node-team {
  font-size: 11px;
  line-height: 14px;
}

.relationship-network-map.compact .node-panel-stats {
  margin-top: 10px;
  padding-top: 10px;
  grid-template-columns: repeat(2, 1fr);
}

.relationship-network-map.compact .connections-list {
  margin-top: 10px;
  padding-top: 10px;
}

.relationship-network-map.compact .connections-title {
  margin-bottom: 5px;
  font-size: 11px;
}

.relationship-network-map.compact .connection-row {
  min-height: 38px;
  padding: 6px 0;
  grid-template-columns: 26px minmax(0, 1fr) 50px;
}

.relationship-network-map.compact .connection-avatar {
  width: 26px;
  height: 26px;
}

.relationship-network-map.compact .connection-team {
  display: none;
}

.relationship-network-map.compact .node-panel-actions {
  margin-top: 8px;
  gap: 6px;
}

.network-toolbar {
  min-height: 48px;
  padding: 8px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: #FFFFFF;
  border-bottom: 1px solid #E5E7EB;
}

.network-summary {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #4B5563;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.summary-primary {
  color: #111827;
}

.summary-divider,
.summary-muted {
  color: #9CA3AF;
}

.network-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}

.strength-control {
  width: 282px;
  min-height: 34px;
  padding: 3px;
  border: 1px solid #E5E7EB;
  border-radius: 7px;
  background: #FFFFFF;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 6px;
}

.control-label {
  padding-left: 7px;
  color: #4B5563;
  font-size: 11px;
  font-weight: 800;
  line-height: 14px;
  text-transform: uppercase;
  white-space: nowrap;
}

.link-filter-toggle {
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(78px, 1fr));
  gap: 2px;
}

.link-filter-option {
  min-width: 0;
  min-height: 26px;
  padding: 4px 6px;
  border: none;
  border-radius: 5px;
  background: transparent;
  color: #475569;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 800;
  line-height: 14px;
  transition: background-color 0.16s ease, color 0.16s ease, box-shadow 0.16s ease, opacity 0.16s ease;
}

.link-filter-option span,
.link-filter-option strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.link-filter-option strong {
  flex: none;
  font-size: 10px;
  font-weight: 900;
  opacity: 0.6;
}

.link-filter-option:not(.active):not(:disabled):hover {
  background: var(--relationship-brand-soft);
  color: var(--relationship-brand);
}

.link-filter-option.active {
  background: var(--relationship-brand-soft);
  color: var(--relationship-brand);
  box-shadow: inset 0 0 0 1px var(--relationship-brand-line);
}

.link-filter-option.active strong {
  opacity: 0.78;
}

.link-filter-option.single.active {
  background: #F8FAFC;
  color: #475569;
  box-shadow: inset 0 0 0 1px var(--relationship-brand-line);
}

.link-filter-option:disabled {
  cursor: default;
  opacity: 1;
}

.link-filter-option:focus-visible {
  outline: 2px solid var(--relationship-brand-line);
  outline-offset: 2px;
}

.map-icon-button {
  width: 32px;
  height: 32px;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  background: #FFFFFF;
  color: #374151;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.16s ease, border-color 0.16s ease, color 0.16s ease, box-shadow 0.16s ease;
}

.map-icon-button:hover {
  border-color: var(--relationship-brand-line);
  background: var(--relationship-brand-soft);
  color: var(--relationship-brand);
  box-shadow: 0 4px 10px rgba(108, 48, 204, 0.12);
}

.map-icon-button:focus-visible {
  outline: 2px solid var(--relationship-brand-line);
  outline-offset: 2px;
}

.map-stage-wrap {
  position: relative;
  min-height: 320px;
}

.sigma-stage {
  position: absolute;
  inset: 0;
  z-index: 0;
  background:
    linear-gradient(var(--map-grid-color) 1px, transparent 1px),
    linear-gradient(90deg, var(--map-grid-color) 1px, transparent 1px),
    var(--map-surface);
  background-size: var(--map-grid-size) var(--map-grid-size);
  transition: background-color 0.18s ease;
}

.edge-flow-canvas {
  position: absolute;
  inset: 0;
  z-index: 2;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.map-hud {
  position: absolute;
  top: 14px;
  left: 14px;
  width: min(330px, calc(100% - 28px));
  display: flex;
  flex-direction: column;
  gap: 8px;
  pointer-events: none;
  z-index: 3;
}

.network-insights-card,
.attribute-scale-card,
.network-notice,
.signature-highlight-card {
  border: 1px solid rgba(226, 232, 240, 0.95);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 12px 26px rgba(15, 23, 42, 0.08);
}

.network-insights-card {
  padding: 10px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.insight-item {
  min-width: 0;
  padding: 8px 6px;
  border-radius: 6px;
  background: #F8FAFC;
  text-align: center;
}

.insight-value,
.insight-label {
  display: block;
}

.insight-value {
  color: #111827;
  font-size: 17px;
  line-height: 20px;
  font-weight: 800;
}

.insight-label {
  margin-top: 2px;
  color: #64748B;
  font-size: 11px;
  line-height: 14px;
  font-weight: 700;
  text-transform: uppercase;
}

.network-notice {
  padding: 8px 10px;
  color: #775A00;
  background: rgba(255, 248, 216, 0.95);
  border-color: #F6E7A7;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
}

.signature-highlight-card {
  padding: 8px 10px;
  color: var(--relationship-brand-dark);
  background: rgba(250, 247, 255, 0.96);
  border-color: var(--relationship-brand-line);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  box-shadow: 0 12px 26px rgba(108, 48, 204, 0.1);
  font-size: 12px;
  font-weight: 800;
  line-height: 16px;
}

.signature-highlight-title {
  min-width: 0;
  color: var(--relationship-brand);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.detail-mode-card {
  padding: 8px 10px;
  border: 1px solid rgba(226, 232, 240, 0.95);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.94);
  color: #64748B;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  box-shadow: 0 12px 26px rgba(15, 23, 42, 0.08);
  font-size: 12px;
  font-weight: 800;
  line-height: 16px;
}

.detail-mode-name {
  color: var(--relationship-brand);
}

.edge-legend-card {
  padding: 8px 10px;
  border: 1px solid rgba(226, 232, 240, 0.95);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.94);
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 12px 26px rgba(15, 23, 42, 0.08);
  pointer-events: none;
}

.edge-legend-item {
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #475569;
  font-size: 11px;
  font-weight: 800;
  line-height: 14px;
  text-transform: uppercase;
}

.edge-legend-line {
  width: 30px;
  border-radius: 999px;
  display: inline-block;
  flex: none;
}

.edge-legend-standard {
  height: 2px;
  background: var(--relationship-brand-edge);
}

.edge-legend-strong {
  height: 4px;
  background: var(--relationship-brand);
}

.edge-legend-management {
  height: 3px;
  background: rgba(75, 85, 99, 0.55);
}

.cluster-chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  pointer-events: auto;
}

.cluster-chip {
  max-width: 154px;
  min-height: 28px;
  padding: 5px 7px;
  border: 1px solid #E2E8F0;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.95);
  color: #334155;
  display: inline-grid;
  grid-template-columns: 8px minmax(0, 1fr) auto;
  align-items: center;
  gap: 6px;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
  cursor: pointer;
  font-size: 11px;
  font-weight: 800;
  line-height: 14px;
}

.cluster-chip:hover {
  border-color: var(--relationship-brand-line);
  color: var(--relationship-brand);
  background: var(--relationship-brand-soft);
}

.cluster-chip i {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.cluster-chip span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cluster-chip strong {
  color: #64748B;
  font-weight: 900;
}

.attribute-scale-card {
  position: absolute;
  left: 14px;
  bottom: 14px;
  width: min(238px, calc(100% - 28px));
  padding: 8px 9px;
  z-index: 2;
  border-color: rgba(226, 232, 240, 0.72);
  background: rgba(255, 255, 255, 0.68);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
  backdrop-filter: blur(14px) saturate(145%);
  -webkit-backdrop-filter: blur(14px) saturate(145%);
  pointer-events: none;
}

.scale-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: #64748B;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
}

.scale-header span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scale-legend-list {
  margin-top: 6px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px 7px;
}

.scale-legend-item {
  min-width: 0;
  display: grid;
  grid-template-columns: 7px minmax(0, 1fr) auto;
  align-items: center;
  gap: 4px;
  color: #475569;
  font-size: 9px;
  font-weight: 800;
  line-height: 12px;
}

.scale-legend-item i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.08);
}

.scale-legend-item span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scale-legend-item strong {
  color: #334155;
  font-size: 9px;
  font-weight: 900;
}

.scale-legend-item.muted {
  color: #94A3B8;
}

.scale-legend-item.muted strong {
  color: #64748B;
}

.scale-red {
  background: #F44336;
}

.scale-yellow {
  background: #FBC02D;
}

.scale-green {
  background: #66BB6A;
}

.scale-unknown {
  background: #9CA3AF;
}

.map-empty-state {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: rgba(248, 250, 252, 0.92);
  color: #4B5563;
  pointer-events: none;
}

.map-loading-state {
  gap: 8px;
}

.loading-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid #E5E7EB;
  border-top-color: var(--relationship-brand);
  border-radius: 50%;
  animation: relationship-map-spin 800ms linear infinite;
}

@keyframes relationship-map-spin {
  to {
    transform: rotate(360deg);
  }
}

.empty-title {
  color: #111827;
  font-size: 15px;
  font-weight: 800;
}

.empty-subtitle {
  margin-top: 4px;
  max-width: 280px;
  font-size: 13px;
  line-height: 1.4;
}

.focused-node-anchor {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 4;
  pointer-events: auto;
}

.focused-node-avatar {
  width: 54px;
  height: 54px;
  border: 3px solid #FFFFFF;
  border-radius: 50%;
  padding: 0;
  overflow: hidden;
  background: var(--focused-avatar-color, var(--relationship-brand));
  color: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow:
    0 0 0 3px var(--focused-avatar-color, var(--relationship-brand)),
    0 12px 30px rgba(15, 23, 42, 0.24);
  cursor: pointer;
  font-size: 14px;
  font-weight: 900;
  line-height: 1;
}

.focused-node-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.focus-avatar-enter-active,
.focus-avatar-leave-active {
  transition: opacity 180ms ease;
}

.focus-avatar-enter-active .focused-node-avatar,
.focus-avatar-leave-active .focused-node-avatar {
  transition: transform 180ms ease, box-shadow 180ms ease;
}

.focus-avatar-enter,
.focus-avatar-leave-to {
  opacity: 0;
}

.focus-avatar-enter .focused-node-avatar,
.focus-avatar-leave-to .focused-node-avatar {
  transform: scale(0.72);
  box-shadow:
    0 0 0 1px var(--focused-avatar-color, var(--relationship-brand)),
    0 6px 18px rgba(15, 23, 42, 0.16);
}

.node-panel {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 324px;
  max-width: calc(100% - 32px);
  padding: 14px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 14px 32px rgba(15, 23, 42, 0.13);
}

.node-panel-transition-enter-active,
.node-panel-transition-leave-active {
  transition: opacity 180ms ease, transform 180ms ease;
}

.node-panel-transition-enter,
.node-panel-transition-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

@media (prefers-reduced-motion: reduce) {
  .focus-avatar-enter-active,
  .focus-avatar-leave-active,
  .focus-avatar-enter-active .focused-node-avatar,
  .focus-avatar-leave-active .focused-node-avatar,
  .node-panel-transition-enter-active,
  .node-panel-transition-leave-active {
    transition-duration: 1ms;
  }

  .loading-spinner {
    animation: none;
  }
}

.node-panel-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.node-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  overflow: hidden;
  background: var(--relationship-brand);
  color: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 800;
  flex: none;
}

.node-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.node-copy {
  min-width: 0;
  flex: 1;
}

.node-name {
  color: #111827;
  font-size: 14px;
  font-weight: 800;
  line-height: 18px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.node-team {
  margin-top: 2px;
  color: #6B7280;
  font-size: 12px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.score-badge {
  min-width: 74px;
  max-width: 94px;
  padding: 5px 8px 6px;
  border-radius: 6px;
  text-align: center;
  flex: none;
}

.score-badge-label,
.score-badge strong {
  display: block;
}

.score-badge-label {
  overflow: hidden;
  color: inherit;
  font-size: 8px;
  font-weight: 900;
  line-height: 10px;
  opacity: 0.76;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}

.score-badge strong {
  margin-top: 1px;
  color: inherit;
  font-size: 15px;
  font-weight: 900;
  line-height: 17px;
}

.node-panel-stats {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #EEF2F7;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.stat-value,
.stat-label {
  display: block;
  text-align: center;
}

.stat-value {
  color: #111827;
  font-size: 16px;
  font-weight: 800;
}

.stat-label {
  margin-top: 2px;
  color: #6B7280;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}

.selected-power-row {
  margin-top: 10px;
  padding: 10px 11px;
  border-radius: 6px;
  background: #F8FAFC;
  color: #475569;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.power-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.power-copy span {
  font-size: 12px;
  font-weight: 800;
}

.power-copy small {
  color: #64748B;
  font-size: 10px;
  font-weight: 700;
  line-height: 13px;
}

.selected-power-row strong {
  color: #111827;
  font-size: 14px;
  flex: none;
}

.connections-list {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #EEF2F7;
}

.connections-title {
  margin-bottom: 8px;
  color: #374151;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
}

.connection-row {
  width: 100%;
  min-height: 44px;
  margin: 0;
  padding: 7px 0;
  border: none;
  border-top: 1px solid #F1F5F9;
  background: transparent;
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr) 58px;
  align-items: center;
  gap: 8px;
  text-align: left;
  cursor: pointer;
}

.connection-row:first-of-type {
  border-top: none;
}

.connection-row:hover .connection-name {
  color: var(--relationship-brand);
}

.connection-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  overflow: hidden;
  background: var(--relationship-brand);
  color: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 900;
}

.connection-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.connection-copy {
  min-width: 0;
}

.connection-name,
.connection-team {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.connection-name {
  color: #111827;
  font-size: 12px;
  font-weight: 800;
  line-height: 16px;
}

.connection-team {
  color: #64748B;
  font-size: 11px;
  font-weight: 600;
  line-height: 14px;
}

.connection-strength {
  color: #475569;
  font-size: 11px;
  font-weight: 800;
  text-align: right;
}

.connection-bar {
  margin-top: 4px;
  height: 4px;
  border-radius: 999px;
  overflow: hidden;
  background: #E5E7EB;
}

.connection-bar i {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, var(--relationship-brand-light), var(--relationship-brand));
}

.connections-empty {
  padding: 10px;
  border-radius: 6px;
  background: #F8FAFC;
  color: #64748B;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
}

.node-panel-actions {
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
}

.exit-focus-action {
  margin-right: auto;
  color: var(--relationship-brand);
  border-color: var(--relationship-brand-line);
  background: var(--relationship-brand-soft);
  font-weight: 800;
}

.exit-focus-action:hover,
.exit-focus-action:focus {
  color: #FFFFFF;
  border-color: var(--relationship-brand);
  background: var(--relationship-brand);
}

.blur-sensitive {
  filter: blur(6px);
}

@media (max-width: 820px) {
  .network-toolbar {
    align-items: stretch;
    flex-direction: column;
    gap: 8px;
  }

  .network-controls {
    justify-content: space-between;
  }

  .strength-control {
    width: 100%;
  }

  .map-hud {
    left: 12px;
    right: 12px;
    width: auto;
  }

  .attribute-scale-card {
    left: 12px;
    right: auto;
    width: min(238px, calc(100% - 24px));
  }

  .network-insights-card {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .attribute-scale-card {
    bottom: 12px;
  }

  .relationship-network-map.has-selection .map-hud,
  .relationship-network-map.has-selection .attribute-scale-card {
    display: none;
  }

  .node-panel {
    left: 12px;
    right: 12px;
    top: 12px;
    width: auto;
  }
}
</style>
